import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import BracketPrintView from './BracketPrintView';
import { BracketPayoutModal, SidePotPayoutModal, CleanGamePayoutModal, HighGamePayoutModal, HighSeriesPayoutModal, MysteryScorePayoutModal, MysterySeriesPayoutModal, SurvivorPayoutModal, EliminatorPayoutModal } from './payout';

interface SideEvent {
  id: number;
  name: string;
  description: string;
  type: string;
  applies_to: string;
  cost: number;
  bracket_size?: number;
  league_name: string;
  squad_name?: string;
  squad_date?: string;
}

interface Bowler {
  id: number;
  name: string;
  email: string;
  team_name?: string;
}

interface SideEventEntry {
  id: number;
  bowler_id?: number;
  team_id?: number;
  bowler_name?: string;
  team_name?: string;
  paid: boolean;
}

interface Squad {
  id: number;
  name: string;
  date: string;
  start_time: string;
  end_time: string;
}

interface SideEventStats {
  sideEvent: any;
  totalPot: number;
  totalEntries: number;
  highGameResults: any[];
  highSeriesResults?: any[];
}

interface EntryReceipt {
  success: boolean;
  bowler_name: string;
  team_name?: string;
  side_event_name: string;
  entry_count: number;
  cost_per_entry: number;
  total_cost: number;
  applies_to: string;
  payment_date: string;
  squad_name: string;
  squad_date: string;
}

interface MysteryScoreStats {
  sideEvent: any;
  totalPot: number;
  totalEntries: number;
}

interface MysteryScoreSearchResult {
  mysteryScore: number;
  found: boolean;
  results: Array<{
    score: number;
    game_id: number;
    game_number: number;
    bowler_id?: number;
    bowler_name?: string;
    team_id?: number;
    team_name?: string;
    match_id: number;
    scratchScore?: number;
    handicapScore?: number;
  }>;
}

interface MysterySeriesStats {
  sideEvent: any;
  totalPot: number;
  totalEntries: number;
}

interface MysterySeriesSearchResult {
  mysterySeries: number;
  found: boolean;
  results: Array<{
    total_score: number;
    bowler_id?: number;
    bowler_name?: string;
    team_id?: number;
    team_name?: string;
    match_id: number;
    scratchScore?: number;
    handicapScore?: number;
  }>;
}

interface CleanGameStats {
  sideEvent: any;
  totalPot: number;
  totalEntries: number;
}

interface CleanGameBowlerSearchResult {
  query: string;
  results: Array<{
    id: number;
    name: string;
  }>;
}

interface SurvivorStats {
  sideEvent: any;
  survivorConfig: Array<{
    id: number;
    side_event_id: number;
    game_number: number;
    survivor_score: number;
    award: number;
  }>;
  survivorRounds: Array<{
    gameNumber: number;
    survivorScore: number;
    participantsStart: number;
    survivors: Array<{
      bowler_id?: number;
      team_id?: number;
      bowler_name?: string;
      team_name?: string;
      actualScore: number;
      scratchScore?: number;
      handicapScore?: number;
    }>;
    survivorsCount: number;
    eliminated: number;
  }>;
  totalPot: number;
  totalEntries: number;
}

interface EliminatorStats {
  sideEvent: any;
  eliminatorConfig: Array<{
    id: number;
    side_event_id: number;
    game_number: number;
    advancement_percentage: number;
    round_payout: number;
    final_payout: number;
  }>;
  eliminatorRounds: Array<{
    gameNumber: number;
    advancementPercentage: number;
    participantsStart: number;
    advancers: Array<{
      bowler_id?: number;
      team_id?: number;
      bowler_name?: string;
      team_name?: string;
      actualScore: number;
      scratchScore?: number;
      handicapScore?: number;
    }>;
    advancersCount: number;
    eliminated: number;
    cutoffScore: number;
  }>;
  totalPot: number;
  totalEntries: number;
}

interface SidePotStats {
  sideEvent: any;
  sidePotConfigs: Array<{
    id: number;
    side_event_id: number;
    config_type: 'individual_game' | 'game_series';
    game_identifier: string;
    winners_count: number;
    payout_amount: number;
  }>;
  sidePotResults: Array<{
    configId: number;
    configType: 'individual_game' | 'game_series';
    gameIdentifier: string;
    winnersCount: number;
    payoutAmount: number;
    winners: Array<{
      bowler_id?: number;
      double_id?: number;
      team_id?: number;
      bowler_name?: string;
      team_name?: string;
      totalScore: number;
      scratchScore?: number;
      handicapScore?: number;
      rank: number;
    }>;
    payoutPerWinner: number;
  }>;
  totalPot: number;
  totalEntries: number;
}

interface ManageSideEventProps {
  leagueId: number;
  sideEvent: SideEvent;
  onBack: () => void;
  defaultSquadId?: number;
}

const BACKEND_URL = process.env.REACT_APP_API_URL;

const ManageSideEvent: React.FC<ManageSideEventProps> = ({ leagueId, sideEvent, onBack, defaultSquadId }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Bowler[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<Bowler | null>(null);
  const [entryCount, setEntryCount] = useState(0);
  const [currentEntries, setCurrentEntries] = useState<SideEventEntry[]>([]);
  const [maxEntries, setMaxEntries] = useState<number | null>(null);
  const [bowlerCount, setBowlerCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Squad management state
  const [squads, setSquads] = useState<Squad[]>([]);
  const [selectedSquadId, setSelectedSquadId] = useState<number | null>(defaultSquadId ?? null);
  const [actualSideEventId, setActualSideEventId] = useState<number | null>(null);

  // High game stats state
  const [sideEventStats, setSideEventStats] = useState<SideEventStats | null>(null);
  const [refreshStats, setRefreshStats] = useState(0);

  // Receipt modal state
  const [entryReceipt, setEntryReceipt] = useState<EntryReceipt | null>(null);

  // Mystery score state
  const [mysteryScore, setMysteryScore] = useState<string>('');
  const [mysteryScoreStats, setMysteryScoreStats] = useState<MysteryScoreStats | null>(null);
  const [mysteryScoreResults, setMysteryScoreResults] = useState<MysteryScoreSearchResult | null>(null);

  // Mystery series state
  const [mysterySeries, setMysterySeries] = useState<string>('');
  const [mysterySeriesStats, setMysterySeriesStats] = useState<MysterySeriesStats | null>(null);
  const [mysterySeriesResults, setMysterySeriesResults] = useState<MysterySeriesSearchResult | null>(null);

  // Clean game state
  const [cleanGameQuery, setCleanGameQuery] = useState<string>('');
  const [cleanGameStats, setCleanGameStats] = useState<CleanGameStats | null>(null);
  const [cleanGameSearchResults, setCleanGameSearchResults] = useState<CleanGameBowlerSearchResult | null>(null);
  const [selectedCleanGameWinner, setSelectedCleanGameWinner] = useState<any>(null);

  // Survivor state
  const [survivorStats, setSurvivorStats] = useState<SurvivorStats | null>(null);
  const [editingSurvivorScore, setEditingSurvivorScore] = useState<{ gameNumber: number; score: string } | null>(null);

  // Eliminator state
  const [eliminatorStats, setEliminatorStats] = useState<EliminatorStats | null>(null);
  const [editingEliminatorPercentage, setEditingEliminatorPercentage] = useState<{ gameNumber: number; percentage: string } | null>(null);

  // Side Pot state
  const [sidePotStats, setSidePotStats] = useState<SidePotStats | null>(null);

  // Scratch/Handicap toggle state (for all side events except clean_game and custom)
  // Default to scratch for all, except brackets which default to handicap
  const [isHandicap, setIsHandicap] = useState<boolean>(sideEvent.type === 'bracket');

  // Recalculated survivor rounds based on score type
  const recalculatedSurvivorRounds = React.useMemo(() => {
    if (!survivorStats || sideEvent.type !== 'survivor') return [];

    // We need to recalculate cascading elimination from the beginning
    let currentSurvivors: any[] = [];

    return survivorStats.survivorRounds.map((round, roundIndex) => {
      const participantsToCheck = roundIndex === 0
        ? round.survivors
        : currentSurvivors;

      let recalculatedSurvivors;
      if (sideEvent.applies_to === 'doubles') {
        // For doubles, survivors are doubles (not individual bowlers)
        recalculatedSurvivors = participantsToCheck.filter(survivor => {
          const currentScore = isHandicap
            ? (survivor.handicapScore ?? survivor.actualScore)
            : (survivor.scratchScore ?? survivor.actualScore);
          return currentScore >= round.survivorScore;
        });
        // Only keep unique doubles for next round
        currentSurvivors = recalculatedSurvivors.map(d => ({
          double_id: d.double_id,
          bowler_ids: d.bowler_ids,
          bowler_names: d.bowler_names,
          scratchScore: d.scratchScore,
          handicapScore: d.handicapScore,
          actualScore: d.actualScore,
        }));
      } else {
        // For bowler/team, keep as before
        recalculatedSurvivors = participantsToCheck.filter(survivor => {
          const currentScore = isHandicap
            ? (survivor.handicapScore ?? survivor.actualScore)
            : (survivor.scratchScore ?? survivor.actualScore);
          return currentScore >= round.survivorScore;
        });
        currentSurvivors = recalculatedSurvivors;
      }

      return {
        ...round,
        survivors: recalculatedSurvivors,
        survivorsCount: recalculatedSurvivors.length,
        eliminated: participantsToCheck.length - recalculatedSurvivors.length
      };
    });
  }, [survivorStats, isHandicap, sideEvent.type, sideEvent.applies_to]);

  // Recalculated eliminator rounds based on score type
  const recalculatedEliminatorRounds = React.useMemo(() => {
    if (!eliminatorStats || sideEvent.type !== 'eliminator') return [];

    // We need to recalculate cascading advancement from the beginning
    let currentAdvancers: any[] = [];

    return eliminatorStats.eliminatorRounds.map((round, roundIndex) => {
      // For first round, use all participants from backend  
      // For subsequent rounds, use advancers from previous recalculated round
      const participantsToCheck = roundIndex === 0
        ? round.advancers // First round - use all original participants
        : currentAdvancers; // Subsequent rounds - use cascaded advancers

      // Calculate scores for all current participants
      const participantsWithScores = participantsToCheck.map(participant => ({
        ...participant,
        currentScore: isHandicap
          ? (participant.handicapScore ?? participant.actualScore)
          : (participant.scratchScore ?? participant.actualScore)
      }));

      // Sort by score (highest first)
      participantsWithScores.sort((a, b) => b.currentScore - a.currentScore);

      // Calculate how many advance based on percentage
      const advancersCount = Math.ceil(participantsToCheck.length * (round.advancementPercentage / 100));
      const actualAdvancers = participantsWithScores.slice(0, advancersCount);

      // Determine cutoff score
      const cutoffScore = actualAdvancers.length > 0
        ? actualAdvancers[actualAdvancers.length - 1].currentScore
        : 0;

      // Update currentAdvancers for next round
      if (sideEvent.applies_to === 'doubles') {
        // Only keep unique doubles for next round
        currentAdvancers = actualAdvancers.map(d => ({
          double_id: d.double_id,
          bowler_ids: d.bowler_ids,
          bowler_names: d.bowler_names,
          scratchScore: d.scratchScore,
          handicapScore: d.handicapScore,
          actualScore: d.actualScore,
        }));
      } else {
        currentAdvancers = actualAdvancers;
      }

      return {
        ...round,
        advancers: actualAdvancers,
        advancersCount: actualAdvancers.length,
        eliminated: participantsToCheck.length - actualAdvancers.length,
        cutoffScore
      };
    });
  }, [eliminatorStats, isHandicap, sideEvent.type, sideEvent.applies_to]);

  // Recalculated side pot results based on score type
  const recalculatedSidePotResults = React.useMemo(() => {
    if (!sidePotStats || sideEvent.type !== 'side_pots') return [];

    return sidePotStats.sidePotResults.map(result => {
      let winnersWithScores = result.winners.map(winner => ({
        ...winner,
        currentScore: isHandicap
          ? (winner.handicapScore ?? winner.totalScore)
          : (winner.scratchScore ?? winner.totalScore)
      }));

      // For doubles, ensure only unique doubles are ranked and payout as a pair
      if (sideEvent.applies_to === 'doubles') {
        // Group by double_id to ensure uniqueness
        const doubleMap = new Map();
        for (const winner of winnersWithScores) {
          if (!doubleMap.has(winner.double_id)) {
            doubleMap.set(winner.double_id, winner);
          }
        }
        winnersWithScores = Array.from(doubleMap.values());
      }

      // Sort by score (highest first)
      winnersWithScores.sort((a, b) => b.currentScore - a.currentScore);

      // Reassign ranks based on new sorting
      const rerankedWinners = winnersWithScores.map((winner, index) => ({
        ...winner,
        rank: index + 1
      }));

      // Take only the number of winners specified in the config
      const finalWinners = rerankedWinners.slice(0, result.winnersCount);

      return {
        ...result,
        winners: finalWinners
      };
    });
  }, [sidePotStats, isHandicap, sideEvent.type, sideEvent.applies_to]);

  // Recalculated high series results based on score type
  const recalculatedHighSeriesResults = React.useMemo(() => {
    if (!sideEventStats || sideEvent.type !== 'high_series' || !sideEventStats.highSeriesResults) return [];

    // Get all results with their current scores and check if they have entries
    const resultsWithScores = sideEventStats.highSeriesResults.map((result: any) => ({
      ...result,
      currentScore: isHandicap
        ? (result.handicapScore || result.scratchScore || result.total_score)
        : (result.scratchScore || result.total_score),
      // Check if this participant has paid entries for this side event
      hasEntry: currentEntries.some(entry =>
        sideEvent.applies_to === 'team'
          ? entry.team_id === result.team_id
          : entry.bowler_id === result.bowler_id
      )
    }));

    // Sort by current score (highest first)
    resultsWithScores.sort((a, b) => b.currentScore - a.currentScore);

    return resultsWithScores;
  }, [sideEventStats, isHandicap, sideEvent.type, currentEntries, sideEvent.applies_to]);

  //Recalculated mystery score results based on score type
  const recalculatedMysteryScoreResults = React.useMemo(() => {
    if (!mysteryScoreResults || sideEvent.type !== 'mystery_score') return null;
    const resultsWithScores = mysteryScoreResults.results.map(result => ({
      ...result,
      currentScore: isHandicap
        ? (result.handicapScore || result.score)
        : (result.scratchScore || result.score),
      //check if this participant has paid entries for this side event
      hasEntry: currentEntries.some(entry =>
        sideEvent.applies_to === 'team'
          ? entry.team_id === result.team_id
          : entry.bowler_id === result.bowler_id
      )
    }));
    // Sort by current score (highest first)
    resultsWithScores.sort((a, b) => b.currentScore - a.currentScore);
    return {
      ...mysteryScoreResults,
      results: resultsWithScores
    };
  }, [mysteryScoreResults, isHandicap, sideEvent.type, currentEntries, sideEvent.applies_to]);

  // Recalculated mystery series results based on score type
  const recalculatedMysterySeriesResults = React.useMemo(() => {
    if (!mysterySeriesResults || sideEvent.type !== 'mystery_series') return null;
    const resultsWithScores = mysterySeriesResults.results.map(result => ({
      ...result,
      currentScore: isHandicap
        ? (result.handicapScore || result.total_score)
        : (result.scratchScore || result.total_score),
      //check if this participant has paid entries for this side event
      hasEntry: currentEntries.some(entry =>
        sideEvent.applies_to === 'team'
          ? entry.team_id === result.team_id
          : entry.bowler_id === result.bowler_id
      )
    }));
    // Sort by current score (highest first)
    resultsWithScores.sort((a, b) => b.currentScore - a.currentScore);
    return {
      ...mysterySeriesResults,
      results: resultsWithScores
    };
  }, [mysterySeriesResults, isHandicap, sideEvent.type, currentEntries, sideEvent.applies_to]);

  // Recalculated high game results based on score type
  const recalculatedHighGameResults = React.useMemo(() => {
    if (!sideEventStats || sideEvent.type !== 'high_game' || !sideEventStats.highGameResults) return {};

    // Get all results with their current scores and check if they have entries
    const resultsWithScores = sideEventStats.highGameResults.map((result: any) => ({
      ...result,
      currentScore: isHandicap
        ? (result.handicapScore || result.scratchScore || result.score)
        : (result.scratchScore || result.score),
      // Check if this participant has paid entries for this side event
      hasEntry: currentEntries.some(entry =>
        sideEvent.applies_to === 'team'
          ? entry.team_id === result.team_id
          : entry.bowler_id === result.bowler_id
      )
    }));

    // Group results by result_type and game_number, then find the highest score in each group
    const groupedResults = resultsWithScores.reduce((acc: any, result: any) => {
      const key = result.result_type === 'overall' ? 'overall' : `game_${result.game_number}`;

      // If this is the first result for this key, or if this result has a higher score
      if (!acc[key] || result.currentScore > acc[key].currentScore) {
        acc[key] = result;
      }

      return acc;
    }, {});

    // Convert single results back to arrays for consistency with the UI
    const finalResults: any = {};
    Object.keys(groupedResults).forEach(key => {
      finalResults[key] = [groupedResults[key]]; // Wrap in array since UI expects arrays
    });

    return finalResults;
  }, [sideEventStats, isHandicap, sideEvent.type, currentEntries, sideEvent.applies_to]);

  // Bracket state
  const [bracketStatus, setBracketStatus] = useState<{
    hasStartedBowling: boolean;
    canCreateBrackets: boolean;
    bracketSize: number;
    maxPossibleBrackets: number;
    message: string;
  } | null>(null);

  const [bracketEntries, setBracketEntries] = useState<{
    totalEntries: number;
    currentBracketCount: number;
    bowlerEntries: Array<{
      bowler_id?: number;
      team_id?: number;
      name: string;
      entryCount: number;
      usableEntries?: number;
      refundAmount?: number;
    }>;
  }>({
    totalEntries: 0,
    currentBracketCount: 0,
    bowlerEntries: []
  });

  // Generated brackets state
  const [generatedBrackets, setGeneratedBrackets] = useState<Array<{
    id: number;
    bracket_number: number;
    participants: Array<{
      name: string;
      bowler_id?: number;
      team_id?: number;
      seed: number;
    }>;
  }>>([]);

  const [bracketsGenerated, setBracketsGenerated] = useState(false);

  // Bracket locking state
  const [bracketsLocked, setBracketsLocked] = useState(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [lockModalType, setLockModalType] = useState<'lock' | 'unlock' | 'cannotUnlock' | null>(null);

  // Refunded entries and bracket pot state
  const [refundedEntries, setRefundedEntries] = useState<Array<{
    playerName: string;
    entriesRefunded: number;
  }>>([]);
  const [totalBracketPot, setTotalBracketPot] = useState<number>(0);

  // Bracket scoring state
  const [bracketScoresProcessed, setBracketScoresProcessed] = useState(false);
  const [showScoresModal, setShowScoresModal] = useState(false);
  const [scoresModalType, setScoresModalType] = useState<'missing' | 'processing' | 'confirm' | null>(null);

  // Pull scores confirmation modal state
  const [showPullScoresConfirmModal, setShowPullScoresConfirmModal] = useState(false);

  // Bracket results state
  const [bracketResults, setBracketResults] = useState<{
    winners: Array<{
      position: number;
      entryId: number;
      playerName: string;
      payout: number;
    }>;
    rounds: Array<{
      roundNumber: number;
      gameNumber: number;
      matchups: Array<{
        matchupNumber: number;
        entry1: { id: number; name: string; score: number };
        entry2?: { id: number; name: string; score: number };
        winnerId: number;
      }>;
    }>;
  } | null>(null);

  // Remove side event modal state
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removingEvent, setRemovingEvent] = useState(false);

  // Payout modal state
  const [showPayoutModal, setShowPayoutModal] = useState(false);

  // Side pot payout modal state
  const [showSidePotPayoutModal, setShowSidePotPayoutModal] = useState(false);
  const [selectedSidePotResult, setSelectedSidePotResult] = useState<any>(null);
  // clean game payout modal state
  const [showCleanGamePayoutModal, setShowCleanGamePayoutModal] = useState(false);
  const [selectedCleanGameResult, setSelectedCleanGameResult] = useState<any>(null);
  // high game payout modal state
  const [showHighGamePayoutModal, setShowHighGamePayoutModal] = useState(false);
  const [selectedHighGameResult, setSelectedHighGameResult] = useState<any>(null);
  // high series payout modal state
  const [showHighSeriesPayoutModal, setShowHighSeriesPayoutModal] = useState(false);
  const [selectedHighSeriesResult, setSelectedHighSeriesResult] = useState<any>(null);
  // mystery score payout modal state
  const [showMysteryScorePayoutModal, setShowMysteryScorePayoutModal] = useState(false);
  const [selectedMysteryScoreResult, setSelectedMysteryScoreResult] = useState<any>(null);
  // mystery series payout modal state
  const [showMysterySeriesPayoutModal, setShowMysterySeriesPayoutModal] = useState(false);
  const [selectedMysterySeriesResult, setSelectedMysterySeriesResult] = useState<any>(null);
  // survivor payout modal state
  const [showSurvivorPayoutModal, setShowSurvivorPayoutModal] = useState(false);
  const [selectedSurvivorResult, setSelectedSurvivorResult] = useState<any>(null);
  // eliminator payout modal state
  const [showEliminatorPayoutModal, setShowEliminatorPayoutModal] = useState(false);
  const [selectedEliminatorResult, setSelectedEliminatorResult] = useState<any>(null);

  const totalCost = entryCount * sideEvent.cost;
  const currentEntriesCount = currentEntries.length;

  const fetchCurrentEntries = React.useCallback(async () => {
    if (!actualSideEventId) return; // Wait for actual side event ID

    try {

      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/entries`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentEntries(data);
      }
    } catch (err) {
      console.error('Error fetching entries:', err);
    }
  }, [actualSideEventId]);

  const fetchBowlerCount = React.useCallback(async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/leagues/${leagueId}/bowlers/count`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setBowlerCount(data.count);
      }
    } catch (err) {
      console.error('Error fetching bowler count:', err);
    }
  }, [leagueId]);

  const fetchMaxDoublesEntries = React.useCallback(async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/leagues/${leagueId}/doubles/max-entries`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMaxEntries(data.maxEntries);
      }
    } catch (err) {
      console.error('Error fetching max doubles entries:', err);
    }
  }, [leagueId]);

  const fetchMaxBracketEntries = React.useCallback(async () => {
    if (!actualSideEventId) return; // Wait for actual side event ID

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/bracket/max-entries`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMaxEntries(data.maxEntries);
      } else {
        console.warn('Could not fetch bracket max entries:', response.status);
        // Fallback to default bracket size calculation
        setMaxEntries(Math.floor(bowlerCount / 8) * 8);
      }
    } catch (err) {
      console.error('Error fetching max bracket entries:', err);
      // Fallback to default bracket size calculation
      setMaxEntries(Math.floor(bowlerCount / 8) * 8);
    }
  }, [actualSideEventId, bowlerCount]);

  const fetchSquads = React.useCallback(async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      // Use the management endpoint which includes squads data
      const response = await fetch(`${apiUrl}/admin/leagues/${leagueId}/management`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();

        if (data.squads && Array.isArray(data.squads)) {
          setSquads(data.squads);
          // Auto-select the first squad if none selected
          if (!selectedSquadId && data.squads.length > 0) {
            setSelectedSquadId(data.squads[0].id);
          }
        }
      } else {
        console.warn('Could not fetch squads:', response.status);
      }
    } catch (err) {
      console.error('Error fetching squads:', err);
    }
  }, [leagueId, selectedSquadId]);

  // Clear data when squad changes
  React.useEffect(() => {
    setBracketEntries({ totalEntries: 0, currentBracketCount: 0, bowlerEntries: [] });
    setBracketScoresProcessed(false);
    setBracketResults(null);
    setGeneratedBrackets([]);
  }, [selectedSquadId]);

  const fetchActualSideEventId = React.useCallback(async () => {
    if (!selectedSquadId) return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      // Find the actual side event ID for this squad and side event name
      const response = await fetch(`${apiUrl}/admin/leagues/${leagueId}/side-events?squadId=${selectedSquadId}&name=${encodeURIComponent(sideEvent.name)}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          setActualSideEventId(data[0].id);
        }
      } else {
        console.warn('Could not fetch actual side event ID:', response.status);
      }
    } catch (err) {
      console.error('Error fetching actual side event ID:', err);
    }
  }, [leagueId, selectedSquadId, sideEvent.name]);

  const fetchHighGameStats = React.useCallback(async () => {
    if (!actualSideEventId || sideEvent.type !== 'high_game') return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/high-game-stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSideEventStats(data);
      } else {
        console.warn('Could not fetch high game stats:', response.status);
      }
    } catch (err) {
      console.error('Error fetching high game stats:', err);
    }
  }, [actualSideEventId, sideEvent.type]);

  const fetchHighSeriesStats = React.useCallback(async () => {
    if (!actualSideEventId || sideEvent.type !== 'high_series') return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/high-series-stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSideEventStats(data);
      } else {
        console.warn('Could not fetch high series stats:', response.status);
      }
    } catch (err) {
      console.error('Error fetching high series stats:', err);
    }
  }, [actualSideEventId, sideEvent.type]);



  const fetchCleanGameStats = React.useCallback(async () => {
    if (!actualSideEventId || sideEvent.type !== 'clean_game') return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/clean-game-stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCleanGameStats(data);
      } else {
        console.warn('Could not fetch clean game stats:', response.status);
      }
    } catch (err) {
      console.error('Error fetching clean game stats:', err);
    }
  }, [actualSideEventId, sideEvent.type]);

  const searchCleanGameBowlers = React.useCallback(async () => {
    if (!actualSideEventId || !cleanGameQuery.trim() || cleanGameQuery.trim().length < 2) {
      setCleanGameSearchResults(null);
      return;
    }

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/search-clean-game-bowlers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query: cleanGameQuery.trim() })
      });

      if (response.ok) {
        const data = await response.json();
        setCleanGameSearchResults(data);
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to search bowlers');
      }
    } catch (err) {
      console.error('Error searching clean game bowlers:', err);
      setError('Failed to search bowlers');
    }
  }, [actualSideEventId, cleanGameQuery]);

  // Debounce search for clean game bowlers
  React.useEffect(() => {
    if (sideEvent.type === 'clean_game' && cleanGameQuery.trim().length >= 2) {
      const delayedSearch = setTimeout(() => {
        searchCleanGameBowlers();
      }, 500);

      return () => clearTimeout(delayedSearch);
    } else if (cleanGameQuery.trim().length < 2) {
      setCleanGameSearchResults(null);
    }
  }, [cleanGameQuery, sideEvent.type, searchCleanGameBowlers]);

  const fetchSurvivorStats = React.useCallback(async () => {
    if (!actualSideEventId || sideEvent.type !== 'survivor') return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/survivor-stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSurvivorStats(data);
      } else {
        console.warn('Could not fetch survivor stats:', response.status);
      }
    } catch (err) {
      console.error('Error fetching survivor stats:', err);
    }
  }, [actualSideEventId, sideEvent.type]);

  const fetchEliminatorStats = React.useCallback(async () => {
    if (!actualSideEventId || sideEvent.type !== 'eliminator') return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/eliminator-stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setEliminatorStats(data);
      } else {
        console.warn('Could not fetch eliminator stats:', response.status);
      }
    } catch (err) {
      console.error('Error fetching eliminator stats:', err);
    }
  }, [actualSideEventId, sideEvent.type]);

  const fetchSidePotStats = React.useCallback(async () => {
    if (!actualSideEventId || sideEvent.type !== 'side_pots') return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/side-pot-stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSidePotStats(data);
      } else {
        console.warn('Could not fetch side pot stats:', response.status);
      }
    } catch (err) {
      console.error('Error fetching side pot stats:', err);
    }
  }, [actualSideEventId, sideEvent.type]);

  const fetchBracketStatus = React.useCallback(async () => {
    if (!selectedSquadId || sideEvent.type !== 'bracket' || !actualSideEventId) return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/bracket-status?squadId=${selectedSquadId}&bowlerCount=${bowlerCount}&sideEventId=${actualSideEventId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setBracketStatus(data);
      } else {
        console.warn('Could not fetch bracket status:', response.status);
      }
    } catch (err) {
      console.error('Error fetching bracket status:', err);
    }
  }, [selectedSquadId, sideEvent.type, bowlerCount, actualSideEventId]);

  const fetchBracketEntries = React.useCallback(async () => {
    if (!actualSideEventId || !selectedSquadId || sideEvent.type !== 'bracket') return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/bracket-entries?squadId=${selectedSquadId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();

        // Calculate bracket count logic
        let currentBracketCount = 0;

        if (data.bowlerEntries.length > 0) {
          // If there's only one participant with all entries, they can't play themselves
          if (data.bowlerEntries.length === 1) {
            currentBracketCount = 0; // Can't make a bracket with just one person
          } else {
            // Correct algorithm: The number of brackets is limited by the participant
            // with the fewest entries, since each person can only appear once per bracket
            const minEntriesPerParticipant = Math.min(...data.bowlerEntries.map((p: any) => p.entryCount));
            currentBracketCount = minEntriesPerParticipant;

            // Additional validation: make sure we have enough total entries
            // Each bracket needs at least 2 people (can have byes for the rest)
            const requiredEntriesForBrackets = currentBracketCount * 2; // minimum 2 people per bracket
            if (data.totalEntries < requiredEntriesForBrackets) {
              currentBracketCount = Math.floor(data.totalEntries / 2);
            }
          }
        }

        setBracketEntries({
          totalEntries: data.totalEntries,
          currentBracketCount,
          bowlerEntries: data.bowlerEntries
        });
      } else {
        console.warn('Could not fetch bracket entries:', response.status);
      }
    } catch (err) {
      console.error('Error fetching bracket entries:', err);
    }
  }, [actualSideEventId, selectedSquadId, sideEvent.type]);

  const generateUIBrackets = React.useCallback((brackets: any[], bowlerEntries?: any[]) => {
    const totalBrackets = brackets.length;
    const participantsPerBracket = bracketStatus?.bracketSize || 8; // Use actual bracket size from config

    // Use passed bowlerEntries or current state
    const currentBowlerEntries = bowlerEntries || bracketEntries.bowlerEntries;

    // Step 1: Calculate refunds and usable entries
    const processedBowlers = currentBowlerEntries.map(bowler => {
      const maxUsableEntries = totalBrackets; // Can appear at most once per bracket
      const usableEntries = Math.min(bowler.entryCount, maxUsableEntries);
      const refundAmount = bowler.entryCount - usableEntries;

      return {
        ...bowler,
        usableEntries,
        refundAmount
      };
    });

    // Step 2: Create participant pool ensuring each bowler appears at least once in each bracket
    const participantPool: Array<{ name: string; bowler_id?: number; team_id?: number; bracketAssignment?: number; }> = [];

    processedBowlers.forEach(bowler => {
      // Each bowler must appear exactly once in each of their usable entries
      for (let bracketIndex = 0; bracketIndex < bowler.usableEntries; bracketIndex++) {
        participantPool.push({
          name: bowler.name,
          bowler_id: bowler.bowler_id,
          team_id: bowler.team_id,
          bracketAssignment: bracketIndex
        });
      }
    });

    // Step 3: Randomize participants within their bracket assignments
    const bracketParticipantGroups: Array<Array<any>> = Array(totalBrackets).fill(null).map(() => []);

    participantPool.forEach(participant => {
      if (participant.bracketAssignment !== undefined) {
        bracketParticipantGroups[participant.bracketAssignment].push(participant);
      }
    });

    // Shuffle participants within each bracket group
    bracketParticipantGroups.forEach(group => {
      for (let i = group.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [group[i], group[j]] = [group[j], group[i]];
      }
    });

    // Step 4: Build UI brackets
    const uiBrackets = brackets.map((bracket, bracketIndex) => {
      const bracketParticipants = [];
      const assignedParticipants = bracketParticipantGroups[bracketIndex] || [];

      for (let i = 0; i < participantsPerBracket; i++) {
        if (assignedParticipants[i]) {
          bracketParticipants.push({
            ...assignedParticipants[i],
            seed: i + 1
          });
        }
        // If no participant available, slot remains empty (BYE)
      }

      return {
        id: bracket.id,
        bracket_number: bracket.bracket_number,
        participants: bracketParticipants
      };
    });

    setGeneratedBrackets(uiBrackets);

    // Only update bracket entries with refund info if this is initial generation (not reshuffle)
    if (!bowlerEntries && processedBowlers.some(bowler => bowler.refundAmount > 0)) {
      setBracketEntries(prev => ({
        ...prev,
        bowlerEntries: processedBowlers
      }));
    }
  }, [bracketEntries.bowlerEntries, bracketStatus?.bracketSize]);

  // Fetch bracket results when scores are processed
  const fetchBracketResults = React.useCallback(async () => {
    if (!actualSideEventId || !selectedSquadId || sideEvent.type !== 'bracket') return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/bracket-results?squadId=${selectedSquadId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Store the results for display
        console.log('Bracket results:', data.results);
        setBracketResults(data.results);
      } else if (response.status === 404) {
        // 404 is expected when brackets haven't been scored yet - don't log as error
        console.log('No bracket results available yet (brackets not scored)');
        setBracketResults(null);
      } else {
        console.warn('Could not fetch bracket results:', response.status);
      }
    } catch (err) {
      console.error('Error fetching bracket results:', err);
    }
  }, [actualSideEventId, selectedSquadId, sideEvent.type]);

  const checkExistingBrackets = React.useCallback(async () => {
    if (!actualSideEventId || sideEvent.type !== 'bracket') return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/existing-brackets`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();

        if (data.brackets && data.brackets.length > 0) {
          // Brackets exist - show delete button and display them
          setBracketsGenerated(true);
          setBracketsLocked(data.locked || false); // Set lock status
          setBracketScoresProcessed(data.scoresProcessed || false); // Set scores status

          // Set total bracket pot if available
          if (data.totalBracketPot !== undefined) {
            setTotalBracketPot(Number(data.totalBracketPot) || 0);
          }

          generateUIBrackets(data.brackets);

          // If scores are processed, fetch the detailed results
          if (data.scoresProcessed && selectedSquadId) {
            fetchBracketResults();
          }
        } else {
          // No brackets - show generate button
          setBracketsGenerated(false);
          setBracketsLocked(false);
          setBracketScoresProcessed(false);
          setGeneratedBrackets([]);
          setTotalBracketPot(0); // Reset pot when no brackets
        }
      }
    } catch (err) {
      console.error('Error checking existing brackets:', err);
    }
  }, [actualSideEventId, selectedSquadId, sideEvent.type, generateUIBrackets, fetchBracketResults]);

  const reshuffleBrackets = () => {
    // Re-run the bracket generation with the same bracket structure but new randomization
    if (generatedBrackets.length > 0) {
      const bracketStructure = generatedBrackets.map(b => ({ id: b.id, bracket_number: b.bracket_number }));
      // Pass current bowler entries to prevent state updates during reshuffle
      generateUIBrackets(bracketStructure, bracketEntries.bowlerEntries);
    }
  };

  const generateBrackets = async () => {
    if (!actualSideEventId || bracketEntries.currentBracketCount === 0) return;

    setLoading(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/generate-brackets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bracketCount: bracketEntries.currentBracketCount
        })
      });

      if (response.ok) {
        const data = await response.json();
        setBracketsGenerated(true);

        // Handle refunded entries and bracket pot
        if (data.refundedEntries && data.refundedEntries.length > 0) {
          setRefundedEntries(data.refundedEntries);
        }
        if (data.totalBracketPot !== undefined) {
          setTotalBracketPot(Number(data.totalBracketPot) || 0);
        }

        // Generate UI brackets with seeding
        generateUIBrackets(data.brackets);

        // Refresh bracket entries to reflect the changes after overbought removal
        fetchBracketEntries();

      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error generating brackets');
      }
    } catch (err) {
      console.error('Error generating brackets:', err);
      setError('Error generating brackets');
    } finally {
      setLoading(false);
    }
  };

  const deleteBrackets = async () => {
    if (!actualSideEventId) return;

    setLoading(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/delete-brackets`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setBracketsGenerated(false);
        setGeneratedBrackets([]);
        setRefundedEntries([]); // Clear refunded entries
        setTotalBracketPot(0);  // Clear bracket pot

      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error deleting brackets');
      }
    } catch (err) {
      console.error('Error deleting brackets:', err);
      setError('Error deleting brackets');
    } finally {
      setLoading(false);
    }
  };

  // Lock brackets function
  const lockBrackets = async () => {
    if (!actualSideEventId || !generatedBrackets.length) return;

    setLoading(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/lock-brackets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          brackets: generatedBrackets.map(bracket => ({
            bracket_id: bracket.id,
            bracket_number: bracket.bracket_number,
            participants: bracket.participants
          }))
        })
      });

      if (response.ok) {
        setBracketsLocked(true);
        setShowLockModal(false);
        setLockModalType(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error locking brackets');
      }
    } catch (err) {
      console.error('Error locking brackets:', err);
      setError('Error locking brackets');
    } finally {
      setLoading(false);
    }
  };

  // Unlock brackets function
  const unlockBrackets = async () => {
    if (!actualSideEventId) return;

    setLoading(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/unlock-brackets`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setBracketsLocked(false);
        setShowUnlockModal(false);
        setLockModalType(null);
      } else {
        const errorData = await response.json();
        if (errorData.error && errorData.error.includes('cannot be unlocked after bowling has started')) {
          setLockModalType('cannotUnlock');
          setShowUnlockModal(true);
        } else {
          setError(errorData.error || 'Error unlocking brackets');
        }
      }
    } catch (err) {
      console.error('Error unlocking brackets:', err);
      setError('Error unlocking brackets');
    } finally {
      setLoading(false);
    }
  };

  // Handle lock bracket confirmation
  const handleLockBrackets = () => {
    setLockModalType('lock');
    setShowLockModal(true);
  };

  // Handle unlock bracket confirmation
  const handleUnlockBrackets = () => {
    setLockModalType('unlock');
    setShowUnlockModal(true);
  };

  // Pull scores and process bracket results
  // Show pull scores confirmation modal
  const handlePullScores = () => {
    setShowPullScoresConfirmModal(true);
  };

  // Actually pull the scores with the selected score type
  const confirmPullScores = async () => {
    if (!actualSideEventId || !selectedSquadId) return;

    setLoading(true);
    setShowPullScoresConfirmModal(false);

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/pull-bracket-scores`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          squadId: selectedSquadId,
          scoreType: isHandicap ? 'handicap' : 'scratch'
        })
      });

      if (response.ok) {
        setBracketScoresProcessed(true);
        setError(null);

        // Fetch updated bracket data to show scores
        checkExistingBrackets();

      } else {
        const errorData = await response.json();
        if (errorData.error === 'scores_missing') {
          setScoresModalType('missing');
          setShowScoresModal(true);
        } else {
          setError(errorData.error || 'Error processing bracket scores');
        }
      }
    } catch (err) {
      console.error('Error pulling bracket scores:', err);
      setError('Error processing bracket scores');
    } finally {
      setLoading(false);
    }
  };

  // Effects and other functions

  const updateSurvivorScore = async (gameNumber: number, newScore: number) => {
    if (!actualSideEventId) return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/survivor-score`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ gameNumber, survivorScore: newScore })
      });

      if (response.ok) {
        // Update the local survivor stats directly without backend refetch
        // This preserves our toggle state and prevents backend override
        if (survivorStats) {
          const updatedSurvivorStats = {
            ...survivorStats,
            survivorConfig: survivorStats.survivorConfig.map(config =>
              config.game_number === gameNumber
                ? { ...config, survivor_score: newScore }
                : config
            ),
            survivorRounds: survivorStats.survivorRounds.map(round =>
              round.gameNumber === gameNumber
                ? { ...round, survivorScore: newScore }
                : round
            )
          };
          setSurvivorStats(updatedSurvivorStats);
        }

        setEditingSurvivorScore(null);
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update survivor score');
      }
    } catch (err) {
      console.error('Error updating survivor score:', err);
      setError('Failed to update survivor score');
    }
  };

  const handleSurvivorScoreEdit = (gameNumber: number, currentScore: number) => {
    setEditingSurvivorScore({ gameNumber, score: currentScore.toString() });
  };

  const handleSurvivorScoreSave = async () => {
    if (!editingSurvivorScore) return;

    const newScore = parseInt(editingSurvivorScore.score);
    if (isNaN(newScore) || newScore < 0 || newScore > 1200) {
      setError('Survivor score must be between 0 and 1200');
      return;
    }

    await updateSurvivorScore(editingSurvivorScore.gameNumber, newScore);
  };

  const handleSurvivorScoreCancel = () => {
    setEditingSurvivorScore(null);
    setError(null);
  };

  // Eliminator edit functions
  const updateEliminatorPercentage = async (gameNumber: number, newPercentage: number) => {
    if (!actualSideEventId) return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/eliminator-percentage`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ gameNumber, advancementPercentage: newPercentage })
      });

      if (response.ok) {
        // Update the local eliminator stats directly without backend refetch
        // This preserves our toggle state and prevents backend override
        if (eliminatorStats) {
          const updatedEliminatorStats = {
            ...eliminatorStats,
            eliminatorConfig: eliminatorStats.eliminatorConfig.map(config =>
              config.game_number === gameNumber
                ? { ...config, advancement_percentage: newPercentage }
                : config
            ),
            eliminatorRounds: eliminatorStats.eliminatorRounds.map(round =>
              round.gameNumber === gameNumber
                ? { ...round, advancementPercentage: newPercentage }
                : round
            )
          };
          setEliminatorStats(updatedEliminatorStats);
        }

        setEditingEliminatorPercentage(null);
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update advancement percentage');
      }
    } catch (err) {
      console.error('Error updating advancement percentage:', err);
      setError('Failed to update advancement percentage');
    }
  };

  const handleEliminatorPercentageEdit = (gameNumber: number, currentPercentage: number) => {
    setEditingEliminatorPercentage({ gameNumber, percentage: currentPercentage.toString() });
  };

  const handleEliminatorPercentageSave = async () => {
    if (!editingEliminatorPercentage) return;

    const newPercentage = parseInt(editingEliminatorPercentage.percentage);
    if (isNaN(newPercentage) || newPercentage < 25 || newPercentage > 75) {
      setError('Advancement percentage must be between 25% and 75%');
      return;
    }

    await updateEliminatorPercentage(editingEliminatorPercentage.gameNumber, newPercentage);
  };

  const handleEliminatorPercentageCancel = () => {
    setEditingEliminatorPercentage(null);
    setError(null);
  };

  // Remove side event function
  const removeSideEvent = async () => {
    // For removal, use the original side event ID if no squad-specific ID is found
    const sideEventIdToRemove = actualSideEventId || sideEvent.id;

    if (!sideEventIdToRemove) {
      setError('Side event ID not found');
      return;
    }

    setRemovingEvent(true);
    setError(null);

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${sideEventIdToRemove}/remove`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Navigate back to ManagingLeague
        onBack();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error removing side event');
      }
    } catch (err) {
      console.error('Error removing side event:', err);
      setError('Error removing side event');
    } finally {
      setRemovingEvent(false);
      setShowRemoveModal(false);
    }
  };

  const enterBowlerWithPayment = async () => {
    if (!selectedPlayer || !actualSideEventId || entryCount === 0) {
      setError('Please select a player and set entry count');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/enter-bowler`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bowlerId: selectedPlayer.id,
          entryCount: entryCount
        })
      });

      if (response.ok) {
        const receiptData = await response.json();

        // Reset form
        setSelectedPlayer(null);
        setEntryCount(0);
        setSearchQuery('');

        // Show receipt modal
        setEntryReceipt(receiptData);

        // Refresh data
        await fetchCurrentEntries();
        await fetchHighGameStats();
        await fetchHighSeriesStats();
        await fetchMysteryScoreStats();
        await fetchMysterySeriesStats();
        await fetchCleanGameStats();
        await fetchSurvivorStats();
        await fetchEliminatorStats();
        await fetchSidePotStats();
        if (sideEvent.type === 'bracket') {
          await fetchBracketStatus();
          await fetchBracketEntries();
        }
        setRefreshStats(prev => prev + 1);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error entering bowler');
      }
    } catch (err) {
      console.error('Error entering bowler:', err);
      setError('Error entering bowler');
    } finally {
      setLoading(false);
    }
  };

  const fetchMysteryScoreStats = React.useCallback(async () => {
    if (!actualSideEventId || sideEvent.type !== 'mystery_score') return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/mystery-score-stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMysteryScoreStats(data);
      } else {
        console.warn('Could not fetch mystery score stats:', response.status);
      }
    } catch (err) {
      console.error('Error fetching mystery score stats:', err);
    }
  }, [actualSideEventId, sideEvent.type]);

  const generateRandomMysteryScore = () => {
    let min = 100, max = 300;
    if (sideEvent.applies_to === 'doubles') {
      min = 200; max = 600;
    } else if (sideEvent.applies_to === 'team') {
      min = 200; max = 1200;
    }
    const randomScore = Math.floor(Math.random() * (max - min + 1)) + min;
    setMysteryScore(randomScore.toString());
  };

  const findMysteryScore = async () => {
    if (!actualSideEventId || !mysteryScore) return;

    const scoreValue = parseInt(mysteryScore);
    let minScore = 100, maxScore = 300;
    if (sideEvent.applies_to === 'doubles') {
      minScore = 200; maxScore = 600;
    } else if (sideEvent.applies_to === 'team') {
      minScore = 200; maxScore = 1200;
    }

    if (isNaN(scoreValue) || scoreValue < minScore || scoreValue > maxScore) {
      setError(`Mystery score must be between ${minScore} and ${maxScore}`);
      return;
    }

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/find-mystery-score`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mysteryScore: scoreValue })
      });

      if (response.ok) {
        const data = await response.json();
        setMysteryScoreResults(data);
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to find mystery score');
      }
    } catch (err) {
      console.error('Error finding mystery score:', err);
      setError('Failed to find mystery score');
    }
  };

  const fetchMysterySeriesStats = React.useCallback(async () => {
    if (!actualSideEventId || sideEvent.type !== 'mystery_series') return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/mystery-series-stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMysterySeriesStats(data);
      } else {
        console.warn('Could not fetch mystery series stats:', response.status);
      }
    } catch (err) {
      console.error('Error fetching mystery series stats:', err);
    }
  }, [actualSideEventId, sideEvent.type]);

  const generateRandomMysterySeries = () => {
    let min = 100, max = 900;
    if (sideEvent.applies_to === 'doubles') {
      min = 200; max = 1800;
    } else if (sideEvent.applies_to === 'team') {
      min = 200; max = 3600;
    }
    const randomSeries = Math.floor(Math.random() * (max - min + 1)) + min;
    setMysterySeries(randomSeries.toString());
  };

  const findMysterySeries = async () => {
    if (!actualSideEventId || !mysterySeries) return;

    const seriesValue = parseInt(mysterySeries);
    if (isNaN(seriesValue) || seriesValue < 100 || seriesValue > 3600) {
      setError('Mystery series must be between 100 and 3600');
      return;
    }

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/find-mystery-series`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mysterySeries: seriesValue })
      });

      if (response.ok) {
        const data = await response.json();
        setMysterySeriesResults(data);
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to find mystery series');
      }
    } catch (err) {
      console.error('Error finding mystery series:', err);
      setError('Failed to find mystery series');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchSquads();
      await fetchActualSideEventId(); // Fetch the actual side event ID first
      await fetchCurrentEntries();
      await fetchBowlerCount();
      await fetchHighGameStats(); // Fetch high game stats if applicable
      await fetchHighSeriesStats(); // Fetch high series stats if applicable
      await fetchMysteryScoreStats(); // Fetch mystery score stats if applicable
      await fetchMysterySeriesStats(); // Fetch mystery series stats if applicable
      await fetchCleanGameStats(); // Fetch clean game stats if applicable
      await fetchSurvivorStats(); // Fetch survivor stats if applicable
      await fetchEliminatorStats(); // Fetch eliminator stats if applicable
      await fetchSidePotStats(); // Fetch side pot stats if applicable
      await fetchBracketStatus(); // Fetch bracket status if applicable
      await fetchBracketEntries(); // Fetch bracket entries if applicable
      if (sideEvent.applies_to === 'doubles' && sideEvent.type !== 'bracket') {
        await fetchMaxDoublesEntries();
      } else if (sideEvent.type === 'bracket') {
        await fetchMaxBracketEntries();
      }
    };

    loadData();
  }, [sideEvent.id, sideEvent.applies_to, sideEvent.type, leagueId, selectedSquadId, refreshStats, fetchSquads, fetchActualSideEventId, fetchCurrentEntries, fetchBowlerCount, fetchHighGameStats, fetchHighSeriesStats, fetchMysteryScoreStats, fetchMysterySeriesStats, fetchCleanGameStats, fetchSurvivorStats, fetchEliminatorStats, fetchSidePotStats, fetchBracketStatus, fetchBracketEntries, fetchMaxDoublesEntries, fetchMaxBracketEntries]);

  // Separate useEffect for checking existing brackets
  useEffect(() => {
    if (sideEvent.type === 'bracket' && actualSideEventId) {
      checkExistingBrackets();
    }
  }, [sideEvent.type, actualSideEventId, checkExistingBrackets]);

  const searchBowlers = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/manage-side-events/leagues/${leagueId}/bowlers/search?query=${encodeURIComponent(searchQuery)}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      }
    } catch (err) {
      console.error('Error searching bowlers:', err);
      setError('Error searching bowlers');
    } finally {
      setLoading(false);
    }
  };

  const selectPlayer = (bowler: Bowler) => {
    setSelectedPlayer(bowler);
    setSearchResults([]);
    setSearchQuery('');
    setEntryCount(0); // Reset entry count when selecting a new player
  };

  // Calculate max allowed entries for bracket events based on bracket status
  const getBracketMaxAllowed = () => {
    if (sideEvent.type !== 'bracket' || !bracketStatus) return null;

    if (bracketStatus.hasStartedBowling) {
      return 0; // No new entries allowed
    }

    if (!bracketStatus.canCreateBrackets) {
      return 0; // Can't create brackets
    }

    // Calculate max entries based on bracket size and current player entries
    const bracketSize = bracketStatus.bracketSize;
    const currentPlayerEntries = selectedPlayer ?
      currentEntries.filter(entry => entry.bowler_id === selectedPlayer.id).length : 0;

    // For brackets, allow multiple entries per player up to bracket size
    // This prevents a player from facing themselves in the same bracket
    const maxEntriesPerPlayer = bracketSize;

    return Math.max(0, maxEntriesPerPlayer - currentPlayerEntries);
  };

  const adjustEntryCount = (delta: number) => {
    const newCount = Math.max(0, entryCount + delta);

    // Apply max entries limit based on event type
    let maxAllowed = null;

    if (sideEvent.type === 'bracket') {
      maxAllowed = getBracketMaxAllowed();
    } else if (sideEvent.applies_to === 'doubles' && sideEvent.type !== 'bracket') {
      maxAllowed = maxEntries;
    } else if (sideEvent.applies_to === 'bowler' || sideEvent.applies_to === 'team') {
      maxAllowed = 1; // Single entry only
    }

    if (maxAllowed !== null && newCount > maxAllowed) {
      return;
    }

    setEntryCount(newCount);
  };

  const renderDoublesOverflowWarning = () => {
    if (sideEvent.applies_to !== 'doubles' || sideEvent.type === 'bracket') return null;

    return (
      <div className="text-blue-300 text-sm mt-2">
        ℹ️ Overflow entries will be refunded based on the total number of entries before starting the event
      </div>
    );
  };

  useEffect(() => {
    if (bracketResults && bracketResults.winners.length > 0 && totalBracketPot > 0) {
      const evenPayout = totalBracketPot / bracketResults.winners.length;

      const updatedWinners = bracketResults.winners.map((winner) => ({
        ...winner,
        payout: evenPayout,
      }));

      setBracketResults({
        ...bracketResults,
        winners: updatedWinners,
      });
    }
  }, [bracketResults, totalBracketPot]);


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="text-white hover:text-blue-300 transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-white">{t('manageSideEvent.title')}</h1>
                <p className="text-blue-200">{sideEvent.league_name}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Side Event Details Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>

              <h2 className="text-xl font-bold text-white">{sideEvent.name}</h2>

              <p className="text-blue-200 text-sm">{sideEvent.description}</p>
              {currentEntriesCount === 0 && (
                <button
                  onClick={() => setShowRemoveModal(true)}
                  className="mt-2 text-red-400 hover:text-red-300 text-sm underline"
                >
                  {t('manageSideEvent.removeSideEvent')}
                </button>
              )}
            </div>
            <div>
              <p className="text-blue-300 text-sm">{t('manageSideEvent.type')}: <span className="text-white">{sideEvent.type}</span></p>
              <p className="text-blue-300 text-sm">{t('manageSideEvent.appliesTo')}: <span className="text-white">{sideEvent.applies_to}</span></p>
              <p className="text-blue-300 text-sm">{t('manageSideEvent.cost')}: <span className="text-white">${sideEvent.cost}</span></p>
              {/* Scratch/Handicap Toggle - Show for all side events except clean_game and custom */}
              {sideEvent.type !== 'clean_game' && sideEvent.type !== 'custom' && sideEvent.type !== 'mystery_score' && sideEvent.type !== 'mystery_series' && (
                <div className="flex items-center space-x-2">
                  <span className={`text-xs font-medium ${!isHandicap ? 'text-white' : 'text-gray-400'}`}>
                    {t('manageSideEvent.scratch')}
                  </span>
                  <button
                    onClick={() => setIsHandicap(!isHandicap)}
                    disabled={sideEvent.type === 'bracket' && bracketScoresProcessed}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ${sideEvent.type === 'bracket' && bracketScoresProcessed
                      ? 'bg-gray-500 cursor-not-allowed'
                      : isHandicap ? 'bg-blue-600' : 'bg-gray-600'
                      }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-200 ${isHandicap ? 'translate-x-5' : 'translate-x-1'
                        }`}
                    />
                  </button>
                  <span className={`text-xs font-medium ${isHandicap ? 'text-white' : 'text-gray-400'}`}>
                    {t('manageSideEvent.handicap')}
                  </span>
                  {sideEvent.type === 'bracket' && bracketScoresProcessed && (
                    <span className="text-xs text-gray-400 ml-2">
                      {t('manageSideEvent.lockedAfterScoring')}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div>
              {sideEvent.squad_name && (
                <>
                  <p className="text-blue-300 text-sm">{t('manageSideEvent.squad')}: <span className="text-white">{sideEvent.squad_name}</span></p>
                  <p className="text-blue-300 text-sm">{t('manageSideEvent.date')}: <span className="text-white">{sideEvent.squad_date}</span></p>
                </>
              )}
              {sideEvent.bracket_size && (
                <p className="text-blue-300 text-sm">{t('manageSideEvent.bracketSize')}: <span className="text-white">{sideEvent.bracket_size}</span></p>
              )}
            </div>
          </div>
        </div>

        {/* Entry Management Section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-6">
          {error && (
            <div className="bg-red-500/20 border border-red-400 rounded-lg p-4 mb-4">
              <p className="text-red-200">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Player Search */}
            <div>
              <label className="block text-white font-semibold mb-2">{t('manageSideEvent.enterPlayerIdOrName')}</label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchBowlers()}
                  className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white placeholder-gray-300"
                  placeholder={t('manageSideEvent.searchByNameOrId')}
                />
                <button
                  onClick={searchBowlers}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition duration-200 disabled:opacity-50"
                >
                  {loading ? t('manageSideEvent.searching') : t('manageSideEvent.search')}
                </button>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-2 bg-white/10 border border-white/20 rounded-lg max-h-40 overflow-y-auto">
                  {searchResults.map((bowler) => (
                    <button
                      key={bowler.id}
                      onClick={() => selectPlayer(bowler)}
                      className="w-full text-left px-4 py-2 hover:bg-white/10 text-white border-b border-white/10 last:border-b-0"
                    >
                      <div className="font-semibold">{bowler.name}</div>
                      <div className="text-sm text-blue-300">{t('manageSideEvent.id')}: {bowler.id} {bowler.team_name && `| ${t('manageSideEvent.team')}: ${bowler.team_name}`}</div>
                    </button>
                  ))}
                </div>
              )}

              {/* Selected Player */}
              {selectedPlayer && (
                <div className="mt-4">
                  <label className="block text-white font-semibold mb-2">{t('manageSideEvent.selectedPlayer')}</label>
                  <div className="bg-green-500/20 border border-green-400 rounded-lg p-3">
                    <div className="text-green-200 font-semibold">{selectedPlayer.name}</div>
                    <div className="text-green-300 text-sm">
                      {t('manageSideEvent.id')}: {selectedPlayer.id} {selectedPlayer.team_name && `| ${t('manageSideEvent.team')}: ${selectedPlayer.team_name}`}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Entry Management */}
            <div>
              {/* Entry Count Controls - Horizontal Layout */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-white font-semibold">{t('manageSideEvent.entryManagement')}</label>

                  {/* Squad Selector */}
                  <div className="flex items-center space-x-2">
                    <span className="text-blue-300 text-sm">{t('manageSideEvent.squad')}:</span>
                    <div
                      className="flex space-x-1 max-w-md overflow-x-auto pb-1"
                      style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent'
                      }}
                    >
                      {squads.map((squad) => (
                        <button
                          key={squad.id}
                          onClick={() => setSelectedSquadId(squad.id)}
                          className={`flex-shrink-0 px-3 py-1.5 rounded-md text-xs font-medium transition duration-200 ${selectedSquadId === squad.id
                            ? 'bg-blue-600 text-white'
                            : 'bg-white/10 text-blue-300 hover:bg-white/20'
                            }`}
                        >
                          {squad.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Add Entry Controls */}
                  <div className="bg-white/10 border border-white/20 rounded-lg p-3">
                    <div className="text-blue-300 text-sm mb-2">{t('manageSideEvent.addEntry')}</div>
                    <div className="flex items-center justify-center space-x-2">
                      <button
                        onClick={() => adjustEntryCount(-1)}
                        disabled={entryCount <= 0}
                        className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white w-8 h-8 rounded transition duration-200 text-sm"
                      >
                        -
                      </button>
                      <span className="text-white font-bold text-lg w-8 text-center">{entryCount}</span>
                      <button
                        onClick={() => adjustEntryCount(1)}
                        disabled={(() => {
                          let maxAllowed = null;
                          if (sideEvent.type === 'bracket') {
                            maxAllowed = getBracketMaxAllowed();
                          } else if (sideEvent.applies_to === 'doubles' && sideEvent.type !== 'bracket') {
                            maxAllowed = maxEntries;
                          } else if (sideEvent.applies_to === 'bowler' || sideEvent.applies_to === 'team') {
                            maxAllowed = 1;
                          }
                          return maxAllowed !== null && entryCount >= maxAllowed;
                        })()}
                        className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white w-8 h-8 rounded transition duration-200 text-sm"
                      >
                        +
                      </button>
                    </div>
                  </div>


                  {/* Cost */}
                  <div className="bg-white/10 border border-white/20 rounded-lg p-3">
                    <div className="text-blue-300 text-sm mb-2">{t('manageSideEvent.cost')}</div>
                    <div className="text-white text-lg font-bold text-center">${totalCost.toFixed(2)}</div>
                  </div>

                  {/* Max Entries Info */}
                  <div className="bg-white/10 border border-white/20 rounded-lg p-3">
                    <div className="text-blue-300 text-sm mb-2">{t('manageSideEvent.maxBowlerEntries')}</div>
                    <div className="text-white text-lg font-bold text-center">
                      {(() => {
                        let maxAllowed = null;
                        if (sideEvent.type === 'bracket') {
                          maxAllowed = getBracketMaxAllowed();
                        } else if (sideEvent.applies_to === 'doubles' && sideEvent.type !== 'bracket') {
                          maxAllowed = maxEntries;
                        } else if (sideEvent.applies_to === 'bowler' || sideEvent.applies_to === 'team') {
                          maxAllowed = 1;
                        }
                        return maxAllowed !== null ? maxAllowed : '∞';
                      })()}
                    </div>
                  </div>
                  {/* Current Entries */}
                  <div className="bg-white/10 border border-white/20 rounded-lg p-3">
                    <div className="text-blue-300 text-sm mb-2">{t('manageSideEvent.currentEventEntries')}</div>
                    <div className="text-white text-lg font-bold text-center">{currentEntriesCount}</div>
                  </div>
                </div>



                {/* Additional Info */}
                <div className="mt-3">
                  {/* Bracket-specific information */}
                  {sideEvent.type === 'bracket' && bracketStatus && (
                    <div className="text-sm text-blue-200">
                      {bracketStatus.hasStartedBowling ? (
                        <div className="bg-red-500/20 border border-red-400 rounded p-2">
                          ⚠️ {t('manageSideEvent.errors.bowlingHasStarted')}
                        </div>
                      ) : !bracketStatus.canCreateBrackets ? (
                        <div className="bg-orange-500/20 border border-orange-400 rounded p-2">
                          ⚠️ {t('manageSideEvent.errors.cannotCreateBrackets')}
                        </div>
                      ) : selectedPlayer && getBracketMaxAllowed() === 0 ? (
                        <div className="bg-yellow-500/20 border border-yellow-400 rounded p-2">
                          📋 {t('manageSideEvent.errors.playerAtMaxEntries', { size: bracketStatus.bracketSize })}
                        </div>
                      ) : (
                        <div className="bg-blue-500/20 border border-blue-400 rounded p-2">
                          💡 {t('manageSideEvent.bracketSize')}: {bracketStatus.bracketSize} | {t('manageSideEvent.maxBowlerEntries')}: {bracketStatus.bracketSize}
                        </div>
                      )}
                    </div>
                  )}

                  {renderDoublesOverflowWarning()}
                </div>
              </div>

              {/* Action Button */}
              <div className="mt-4">
                {sideEvent.type === 'bracket' && bracketsGenerated && (
                  <div className="bg-orange-500/20 border border-orange-400 rounded-lg p-3 mb-3">
                    <div className="text-orange-300 text-sm font-medium">
                      ⚠️ {t('manageSideEvent.alreadyAtMax')}
                    </div>
                  </div>
                )}
                <button
                  onClick={enterBowlerWithPayment}
                  disabled={!selectedPlayer || entryCount === 0 || loading || (sideEvent.type === 'bracket' && bracketsGenerated)}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold py-3 px-4 rounded-lg transition duration-200"
                >
                  {loading ? t('manageSideEvent.processing') : t('manageSideEvent.addEntries')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {sideEvent.applies_to === 'doubles' && actualSideEventId && (
          <div className="mb-2 flex">
            <button
              onClick={() => window.open(`/admin/side-events/${actualSideEventId}/doubles`, '_blank')}
              className="bg-yellow-700/50 hover:bg-yellow-800/50 text-sm text-white border border-yellow-600 font-semibold px-6 py-2 rounded-lg shadow transition duration-200"
            >
              See Doubles for this Event
            </button>
          </div>
        )}


        {/* Event-Specific Management Section */}
        {sideEvent.type === 'high_game' && sideEventStats && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">{t('manageSideEvent.highGameManagement')}</h2>
              <div className="text-right">
                <div className="text-blue-300 text-sm">{t('manageSideEvent.totalPot')}</div>
                <div className="text-white text-2xl font-bold">${Number(sideEventStats.totalPot || 0).toFixed(2)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Total Entries */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-blue-300 text-sm mb-2">{t('manageSideEvent.totalEntries')}</div>
                <div className="text-white text-3xl font-bold">{sideEventStats.totalEntries}</div>
              </div>

              {/* Side Event Details */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-blue-300 text-sm mb-2">{t('manageSideEvent.gameNumbers')}</div>
                <div className="text-white text-lg font-bold">
                  {sideEventStats.sideEvent.applies_to_game_numbers || t('manageSideEvent.allGames')}
                </div>
                <div className="text-blue-300 text-sm mt-2">
                  {sideEventStats.sideEvent.applies_to_series ? t('manageSideEvent.appliesToSeries') : t('manageSideEvent.individualGamesOnly')}
                </div>
              </div>

              {/* Payout Formula */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-blue-300 text-sm mb-2">{t('manageSideEvent.payoutFormula')}</div>
                <div className="text-white text-sm">
                  {sideEventStats.sideEvent.payout_formula || t('manageSideEvent.winnerTakesAll')}
                </div>
              </div>
            </div>

            {/* Current High Game(s) */}
            <div className="bg-white/10 border border-white/20 rounded-lg p-4">
              <h3 className="text-xl font-bold text-white mb-4">{t('manageSideEvent.currentHighGameLeaders')}</h3>

              {Object.keys(recalculatedHighGameResults).length > 0 ? (
                <div className="space-y-4">
                  {/* Use recalculated results which are already grouped and sorted */}
                  {(() => {
                    const gameNumbers = sideEventStats.sideEvent.applies_to_game_numbers?.split(',').map((n: string) => parseInt(n.trim())) || [];

                    return (
                      <>
                        {/* Individual game results */}
                        {gameNumbers.map((gameNum: number) => {
                          const gameKey = `game_${gameNum}`;
                          const gameResults = recalculatedHighGameResults[gameKey] || [];

                          if (gameResults.length === 0) return null;

                          return (
                            <div key={gameKey} className="mb-4">
                              <h4 className="text-lg font-semibold text-blue-300 mb-2">{t('manageSideEvent.gameHighScore', { number: gameNum })}</h4>
                              <div className="space-y-2">
                                {gameResults.map((result: any, index: number) => (
                                  <div key={index} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                                    <div className="flex-1">
                                      <div className="text-white font-bold">
                                        {sideEvent.applies_to === 'team'
                                          ? result.team_name
                                          : sideEvent.applies_to === 'doubles'
                                            ? (result.bowler_names
                                              ? result.bowler_names.join(' & ')
                                              : Array.isArray(result.bowler_names)
                                                ? result.bowler_names.join(' & ')
                                                : t('manageSideEvent.doublesPair'))
                                            : result.bowler_name}
                                      </div>
                                      <div className="flex items-center space-x-2 text-sm">
                                        <span className="text-blue-300">
                                          {t('manageSideEvent.game')} {result.game_number}
                                        </span>
                                        {sideEvent.applies_to !== 'doubles' && (
                                          result.hasEntry ? (
                                            <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded text-xs font-medium">
                                              ✓ {t('manageSideEvent.paidEntry')}
                                            </span>
                                          ) : (
                                            <span className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded text-xs font-medium">
                                              ✗ {t('manageSideEvent.noEntryBadge')}
                                            </span>
                                          )
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                      <div className="text-right">
                                        <div className="text-white font-bold text-xl">
                                          {result.currentScore}
                                        </div>
                                        <div className="text-blue-300 text-xs">
                                          {t('manageSideEvent.highGame')} {isHandicap ? t('manageSideEvent.withHandicap') : t('manageSideEvent.scratchScore')}
                                        </div>
                                      </div>
                                      <button
                                        onClick={() => {
                                          // For doubles, allow payout regardless of hasEntry
                                          if (sideEvent.applies_to === 'doubles' || result.hasEntry) {
                                            const data = {
                                              bowlerId:
                                                sideEvent.applies_to === 'team'
                                                  ? result.team_id
                                                  : sideEvent.applies_to === 'doubles'
                                                    ? result.bowler_ids
                                                    : result.bowler_id,
                                              bowlerName:
                                                sideEvent.applies_to === 'team'
                                                  ? result.team_name
                                                  : sideEvent.applies_to === 'doubles'
                                                    ? (result.bowler_names ? result.bowler_names.join(' & ') : result.bowler_ids?.join(' & '))
                                                    : result.bowler_name,
                                              sideEventId: sideEvent.id,
                                              appliesTo: sideEvent.applies_to,
                                              sideEventType: 'side_event',
                                              amount: 0,
                                              placement: 1,
                                              leagueId: leagueId,
                                            };
                                            setSelectedHighGameResult(data);
                                            setShowHighGamePayoutModal(true);
                                          }
                                        }}
                                        disabled={sideEvent.applies_to !== 'doubles' && !result.hasEntry}
                                        className={`px-3 py-1 rounded text-sm transition duration-200 ${sideEvent.applies_to === 'doubles' || result.hasEntry
                                          ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                          : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                          }`}
                                      >
                                        {sideEvent.applies_to === 'doubles' || result.hasEntry ? t('manageSideEvent.payoutButton') : t('manageSideEvent.noEntryButton')}
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}

                        {/* Overall high across all games */}
                        {recalculatedHighGameResults.overall && recalculatedHighGameResults.overall.length > 0 && (
                          <div className="border-t border-white/20 pt-4">
                            <h4 className="text-lg font-semibold text-yellow-300 mb-2">{t('manageSideEvent.overallHighScore')}</h4>
                            <div className="space-y-2">
                              {recalculatedHighGameResults.overall.map((result: any, index: number) => (
                                <div key={index} className="flex items-center justify-between bg-yellow-500/10 border border-yellow-400/30 rounded-lg p-3">
                                  <div className="flex-1">
                                    <div className="text-white font-bold">
                                      {sideEvent.applies_to === 'team'
                                        ? result.team_name
                                        : sideEvent.applies_to === 'doubles'
                                          ? (result.bowler_names
                                            ? result.bowler_names.join(' & ')
                                            : Array.isArray(result.bowler_ids)
                                              ? result.bowler_ids.join(' & ')
                                              : t('manageSideEvent.doublesPair'))
                                          : result.bowler_name}
                                    </div>
                                    <div className="flex items-center space-x-2 text-sm">
                                      <span className="text-yellow-300">
                                        {t('manageSideEvent.game')} {result.game_number} • {t('manageSideEvent.overallLeader')}
                                      </span>
                                      {sideEvent.applies_to !== 'doubles' && (
                                        result.hasEntry ? (
                                          <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded text-xs font-medium">
                                            ✓ {t('manageSideEvent.paidEntry')}
                                          </span>
                                        ) : (
                                          <span className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded text-xs font-medium">
                                            ✗ {t('manageSideEvent.noEntryBadge')}
                                          </span>
                                        )
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-3">
                                    <div className="text-right">
                                      <div className="text-yellow-300 font-bold text-2xl">
                                        {result.currentScore}
                                      </div>
                                      <div className="text-yellow-400 text-xs">
                                        {t('manageSideEvent.champion')} {isHandicap ? t('manageSideEvent.withHandicap') : t('manageSideEvent.scratchScore')}
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => {
                                        // For doubles, allow payout regardless of hasEntry
                                        if (sideEvent.applies_to === 'doubles' || result.hasEntry) {
                                          const data = {
                                            bowlerId:
                                              sideEvent.applies_to === 'team'
                                                ? result.team_id
                                                : sideEvent.applies_to === 'doubles'
                                                  ? result.bowler_ids
                                                  : result.bowler_id,
                                            bowlerName:
                                              sideEvent.applies_to === 'team'
                                                ? result.team_name
                                                : sideEvent.applies_to === 'doubles'
                                                  ? (result.bowler_names ? result.bowler_names.join(' & ') : result.bowler_ids?.join(' & '))
                                                  : result.bowler_name,
                                            sideEventId: sideEvent.id,
                                            appliesTo: sideEvent.applies_to,
                                            sideEventType: 'side_event',
                                            amount: 0,
                                            placement: 1,
                                            leagueId: leagueId,
                                          };
                                          setSelectedHighGameResult(data);
                                          setShowHighGamePayoutModal(true);
                                        }
                                      }}
                                      disabled={sideEvent.applies_to !== 'doubles' && !result.hasEntry}
                                      className={`px-3 py-1 rounded text-sm transition duration-200 ${sideEvent.applies_to === 'doubles' || result.hasEntry
                                        ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                        : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                        }`}
                                    >
                                      {sideEvent.applies_to === 'doubles' || result.hasEntry ? t('manageSideEvent.payoutButton') : t('manageSideEvent.noEntryButton')}
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-blue-300 text-lg">{t('manageSideEvent.noGamesRecorded')}</div>
                  <div className="text-blue-400 text-sm mt-2">
                    {t('manageSideEvent.scoresWillAppear')}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* High Series Management Section */}
        {sideEvent.type === 'high_series' && sideEventStats && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">{t('manageSideEvent.highSeriesManagement')}</h2>
              <div className="text-right">
                <div className="text-blue-300 text-sm">{t('manageSideEvent.totalPot')}</div>
                <div className="text-white text-2xl font-bold">${Number(sideEventStats.totalPot || 0).toFixed(2)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Total Entries */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-blue-300 text-sm mb-2">{t('manageSideEvent.totalEntries')}</div>
                <div className="text-white text-3xl font-bold">{sideEventStats.totalEntries}</div>
              </div>

              {/* Side Event Details */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-blue-300 text-sm mb-2">{t('manageSideEvent.gameNumbers')}</div>
                <div className="text-white text-lg font-bold">
                  {sideEventStats.sideEvent.applies_to_game_numbers || t('manageSideEvent.allGames')}
                </div>
                <div className="text-blue-300 text-sm mt-2">
                  {sideEventStats.sideEvent.applies_to_series ? t('manageSideEvent.officialSeries') : t('manageSideEvent.gameSum')}
                </div>
              </div>

              {/* Payout Formula */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-blue-300 text-sm mb-2">{t('manageSideEvent.payoutFormula')}</div>
                <div className="text-white text-sm">
                  {sideEventStats.sideEvent.payout_formula || t('manageSideEvent.winnerTakesAll')}
                </div>
              </div>
            </div>

            {/* Current High Series */}
            <div className="bg-white/10 border border-white/20 rounded-lg p-4">
              <h3 className="text-xl font-bold text-white mb-4">{t('manageSideEvent.currentHighSeriesLeaders')}</h3>

              {recalculatedHighSeriesResults && recalculatedHighSeriesResults.length > 0 ? (
                <div className="space-y-3">
                  {recalculatedHighSeriesResults.map((result: any, index: number) => (
                    <div key={index} className="flex items-center justify-between bg-purple-500/10 border border-purple-400/30 rounded-lg p-4">
                      <div className="flex-1">
                        <div className="text-white font-bold text-lg">
                          {sideEvent.applies_to === 'team'
                            ? result.team_name
                            : sideEvent.applies_to === 'doubles'
                              ? (result.bowler_names
                                ? result.bowler_names.join(' & ')
                                : Array.isArray(result.bowler_names)
                                  ? result.bowler_names.join(' & ')
                                  : t('manageSideEvent.doublesPair'))
                              : result.bowler_name}
                        </div>
                        <div className="flex items-center space-x-2 text-sm">
                          <span className="text-purple-300">
                            {index === 0 ? `🏆 ${t('manageSideEvent.seriesChampion')}` : `${index === 1 ? '🥈' : index === 2 ? '🥉' : '🏅'} ${index + 1}${index === 0 ? t('manageSideEvent.st') : index === 1 ? t('manageSideEvent.nd') : index === 2 ? t('manageSideEvent.rd') : t('manageSideEvent.th')} ${t('manageSideEvent.place')}`}
                          </span>
                          {sideEvent.applies_to !== 'doubles' && (
                            result.hasEntry ? (
                              <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded text-xs font-medium">
                                ✓ {t('manageSideEvent.paidEntry')}
                              </span>
                            ) : (
                              <span className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded text-xs font-medium">
                                ✗ {t('manageSideEvent.noEntryBadge')}
                              </span>
                            )
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="text-right">
                          <div className="text-purple-300 font-bold text-2xl">
                            {result.currentScore}
                          </div>
                          <div className="text-purple-400 text-xs">
                            {t('manageSideEvent.seriesTotal')} {isHandicap ? t('manageSideEvent.withHandicap') : t('manageSideEvent.scratchScore')}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            //const scoreType = isHandicap ? 'w/ Handicap' : 'Scratch';
                            //const entryStatus = result.hasEntry ? 'PAID ENTRY' : 'NO ENTRY - DO NOT PAY';
                            if (sideEvent.applies_to === 'doubles' || result.hasEntry) {
                              const data = {
                                bowlerId:
                                  sideEvent.applies_to === 'team'
                                    ? result.team_id
                                    : sideEvent.applies_to === 'doubles'
                                      ? result.bowler_ids
                                      : result.bowler_id,
                                bowlerName:
                                  sideEvent.applies_to === 'team'
                                    ? result.team_name
                                    : sideEvent.applies_to === 'doubles'
                                      ? (result.bowler_names ? result.bowler_names.join(' & ') : result.bowler_ids?.join(' & '))
                                      : result.bowler_name,
                                sideEventId: sideEvent.id,
                                appliesTo: sideEvent.applies_to,
                                sideEventType: 'side_event',
                                amount: 0,
                                placement: 1,
                                leagueId: leagueId,
                              };
                              setSelectedHighSeriesResult(data);
                              setShowHighSeriesPayoutModal(true);
                            }
                          }}
                          disabled={sideEvent.applies_to !== 'doubles' && !result.hasEntry}
                          className={`px-3 py-1 rounded text-sm transition duration-200 ${sideEvent.applies_to === 'doubles' || result.hasEntry
                            ? 'bg-purple-600 hover:bg-purple-700 text-white'
                            : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            }`}
                        >
                          {sideEvent.applies_to === 'doubles' || result.hasEntry ? t('manageSideEvent.payoutButton') : t('manageSideEvent.noEntryButton')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-blue-300 text-lg">{t('manageSideEvent.noSeriesRecorded')}</div>
                  <div className="text-blue-400 text-sm mt-2">
                    {t('manageSideEvent.seriesWillAppear')}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mystery Score Management Section */}
        {sideEvent.type === 'mystery_score' && mysteryScoreStats && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">{t('manageSideEvent.mysteryScoreManagement')}</h2>
              <div className="text-right">
                <div className="text-orange-300 text-sm">{t('manageSideEvent.totalPot')}</div>
                <div className="text-white text-2xl font-bold">${Number(mysteryScoreStats.totalPot || 0).toFixed(2)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Total Entries */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-orange-300 text-sm mb-2">{t('manageSideEvent.totalEntries')}</div>
                <div className="text-white text-3xl font-bold">{mysteryScoreStats.totalEntries}</div>
              </div>

              {/* Side Event Details */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-orange-300 text-sm mb-2">{t('manageSideEvent.gameNumbers')}</div>
                <div className="text-white text-lg font-bold">
                  {mysteryScoreStats.sideEvent.applies_to_game_numbers || t('manageSideEvent.allGames')}
                </div>
                <div className="text-orange-300 text-sm mt-2">
                  {sideEvent.applies_to === 'team' ? t('manageSideEvent.teamScores') : t('manageSideEvent.individualScores')}
                </div>
              </div>

              {/* Mystery Score Search */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-orange-300 text-sm mb-2">{t('manageSideEvent.mysteryScore')}</div>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    min={sideEvent.applies_to === 'doubles' ? 200 : sideEvent.applies_to === 'team' ? 200 : 100}
                    max={sideEvent.applies_to === 'doubles' ? 600 : sideEvent.applies_to === 'team' ? 1200 : 300}
                    value={mysteryScore}
                    onChange={(e) => setMysteryScore(e.target.value)}
                    placeholder={t('manageSideEvent.enterScore')}
                    className="flex-1 bg-gray-800 text-white border border-gray-600 rounded px-3 py-2 text-sm"
                  />
                  <button
                    onClick={generateRandomMysteryScore}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded text-sm transition duration-200"
                  >
                    {t('manageSideEvent.random')}
                  </button>
                </div>
              </div>
            </div>

            {/* Find Mystery Score Section */}
            <div className="bg-white/10 border border-white/20 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-4 mb-4">
                <h3 className="text-xl font-bold text-white">{t('manageSideEvent.findMysteryScore')}</h3>
                <button
                  onClick={findMysteryScore}
                  disabled={
                    !mysteryScore ||
                    (
                      sideEvent.applies_to === 'doubles'
                        ? parseInt(mysteryScore) < 200 || parseInt(mysteryScore) > 600
                        : sideEvent.applies_to === 'team'
                          ? parseInt(mysteryScore) < 200 || parseInt(mysteryScore) > 1200
                          : parseInt(mysteryScore) < 100 || parseInt(mysteryScore) > 300
                    )
                  }
                  className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition duration-200"
                >
                  {t('manageSideEvent.searchForScore')}
                </button>
              </div>

              {/* Search Results */}
              {recalculatedMysteryScoreResults && (
                <div className="mt-4">
                  {recalculatedMysteryScoreResults.found ? (
                    <div className="space-y-3">
                      <div className="text-green-400 font-bold text-lg flex items-center">
                        ✅ {t('manageSideEvent.mysteryScoreFound')}
                      </div>
                      {recalculatedMysteryScoreResults.results.map((result: any, index: number) => (
                        <div key={index} className="flex items-center justify-between bg-green-500/10 border border-green-400/30 rounded-lg p-4">
                          <div className="flex-1">
                            <div className="text-white font-bold text-lg">
                              {sideEvent.applies_to === 'team'
                                ? result.team_name
                                : sideEvent.applies_to === 'doubles'
                                  ? (result.bowler_names
                                    ? result.bowler_names.join(' & ')
                                    : Array.isArray(result.bowler_names)
                                      ? result.bowler_names.join(' & ')
                                      : t('manageSideEvent.doublesPair'))
                                  : result.bowler_name}
                            </div>
                            <div className="text-green-300 text-sm">
                              {t('manageSideEvent.game')} {result.game_number} - Score: {
                                sideEvent.applies_to === 'doubles'
                                  ? result.totalScore
                                  : result.score
                              }
                            </div>
                            <div className="text-green-300 text-sm">
                              {sideEvent.applies_to !== 'doubles' && (
                                result.hasEntry ? (
                                  <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded text-xs font-medium">
                                    ✓ {t('manageSideEvent.paidEntry')}
                                  </span>
                                ) : (
                                  <span className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded text-xs font-medium">
                                    ✗ {t('manageSideEvent.noEntryBadge')}
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              //const scoreType = isHandicap ? 'w/ Handicap' : 'Scratch';
                              //const entryStatus = result.hasEntry ? 'PAID ENTRY' : 'NO ENTRY - DO NOT PAY';
                              // For doubles, allow payout regardless of hasEntry
                              if (sideEvent.applies_to === 'doubles' || result.hasEntry) {
                                const data = {
                                  bowlerId:
                                    sideEvent.applies_to === 'team'
                                      ? result.team_id
                                      : sideEvent.applies_to === 'doubles'
                                        ? result.bowler_ids
                                        : result.bowler_id,
                                  bowlerName:
                                    sideEvent.applies_to === 'team'
                                      ? result.team_name
                                      : sideEvent.applies_to === 'doubles'
                                        ? (result.bowler_names ? result.bowler_names.join(' & ') : result.bowler_ids?.join(' & '))
                                        : result.bowler_name,
                                  sideEventId: sideEvent.id,
                                  appliesTo: sideEvent.applies_to,
                                  sideEventType: 'side_event',
                                  amount: 0,
                                  placement: 1,
                                  leagueId: leagueId,
                                };
                                setSelectedMysteryScoreResult(data);
                                setShowMysteryScorePayoutModal(true);
                              }
                            }}
                            disabled={sideEvent.applies_to !== 'doubles' && !result.hasEntry}
                            className={`px-3 py-1 rounded text-sm transition duration-200 ${sideEvent.applies_to === 'doubles' || result.hasEntry
                              ? 'bg-purple-600 hover:bg-purple-700 text-white'
                              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                              }`}
                          >
                            {sideEvent.applies_to === 'doubles' || result.hasEntry ? t('manageSideEvent.payoutButton') : t('manageSideEvent.noEntryButton')}
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : recalculatedMysteryScoreResults.mysteryScore ? (
                    <div className="text-center py-4">
                      <div className="text-red-400 font-bold text-lg flex items-center justify-center">
                        ❌ {t('manageSideEvent.mysteryScoreNotBowled', { score: recalculatedMysteryScoreResults.mysteryScore })}
                      </div>
                      <div className="text-red-300 text-sm mt-2">
                        {t('manageSideEvent.tryDifferentScore')}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mystery Series Management Section */}
        {sideEvent.type === 'mystery_series' && mysterySeriesStats && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">{t('manageSideEvent.mysterySeriesManagement')}</h2>
              <div className="text-right">
                <div className="text-teal-300 text-sm">{t('manageSideEvent.totalPot')}</div>
                <div className="text-white text-2xl font-bold">${Number(mysterySeriesStats.totalPot || 0).toFixed(2)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Total Entries */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-teal-300 text-sm mb-2">{t('manageSideEvent.totalEntries')}</div>
                <div className="text-white text-3xl font-bold">{mysterySeriesStats.totalEntries}</div>
              </div>

              {/* Side Event Details */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-teal-300 text-sm mb-2">{t('manageSideEvent.seriesType')}</div>
                <div className="text-white text-lg font-bold">
                  {mysterySeriesStats.sideEvent.applies_to_series ? t('manageSideEvent.officialSeries') : t('manageSideEvent.gameSum')}
                </div>
                <div className="text-teal-300 text-sm mt-2">
                  {sideEvent.applies_to === 'team' ? t('manageSideEvent.teamSeries') : t('manageSideEvent.individualSeries')}
                </div>
              </div>

              {/* Mystery Series Search */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-teal-300 text-sm mb-2">{t('manageSideEvent.mysterySeries')}</div>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    min={sideEvent.applies_to === 'doubles' ? 200 : sideEvent.applies_to === 'team' ? 200 : 100}
                    max={sideEvent.applies_to === 'doubles' ? 1800 : sideEvent.applies_to === 'team' ? 3600 : 900}
                    value={mysterySeries}
                    onChange={(e) => setMysterySeries(e.target.value)}
                    placeholder={t('manageSideEvent.enterSeries')}
                    className="flex-1 bg-gray-800 text-white border border-gray-600 rounded px-3 py-2 text-sm"
                  />
                  <button
                    onClick={generateRandomMysterySeries}
                    className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded text-sm transition duration-200"
                  >
                    {t('manageSideEvent.random')}
                  </button>
                </div>
              </div>
            </div>

            {/* Find Mystery Series Section */}
            <div className="bg-white/10 border border-white/20 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-4 mb-4">
                <h3 className="text-xl font-bold text-white">{t('manageSideEvent.findMysterySeries')}</h3>
                <button
                  onClick={findMysterySeries}
                  disabled={
                    !mysterySeries ||
                    (
                      sideEvent.applies_to === 'doubles'
                        ? parseInt(mysterySeries) < 200 || parseInt(mysterySeries) > 1800
                        : sideEvent.applies_to === 'team'
                          ? parseInt(mysterySeries) < 200 || parseInt(mysterySeries) > 3600
                          : parseInt(mysterySeries) < 100 || parseInt(mysterySeries) > 900
                    )
                  }
                  className="bg-teal-600 hover:bg-teal-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition duration-200"
                >
                  Find Series
                </button>
              </div>

              {/* Search Results */}
              {recalculatedMysterySeriesResults && (
                <div className="mt-4">
                  {recalculatedMysterySeriesResults.found ? (
                    <div className="space-y-3">
                      <div className="text-green-400 font-bold text-lg flex items-center">
                        ✅ {t('manageSideEvent.mysterySeriesFound')}
                      </div>
                      {recalculatedMysterySeriesResults.results.map((result: any, index: number) => (
                        <div key={index} className="flex items-center justify-between bg-green-500/10 border border-green-400/30 rounded-lg p-4">
                          <div className="flex-1">
                            <div className="text-white font-bold text-lg">
                              {sideEvent.applies_to === 'team'
                                ? result.team_name
                                : sideEvent.applies_to === 'doubles'
                                  ? (result.bowler_names
                                    ? result.bowler_names.join(' & ')
                                    : Array.isArray(result.bowler_names)
                                      ? result.bowler_names.join(' & ')
                                      : t('manageSideEvent.doublesPair'))
                                  : result.bowler_name}
                            </div>
                            <div className="text-green-300 text-sm">
                              Score: {
                                sideEvent.applies_to === 'doubles'
                                  ? result.totalSeries
                                  : result.total_score
                              }
                            </div>
                            <div className="text-green-300 text-sm">
                              {sideEvent.applies_to !== 'doubles' && (
                                result.hasEntry ? (
                                  <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded text-xs font-medium">
                                    ✓ {t('manageSideEvent.paidEntry')}
                                  </span>
                                ) : (
                                  <span className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded text-xs font-medium">
                                    ✗ {t('manageSideEvent.noEntryBadge')}
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              //const scoreType = isHandicap ? 'w/ Handicap' : 'Scratch';
                              //const entryStatus = result.hasEntry ? 'PAID ENTRY' : 'NO ENTRY - DO NOT PAY';
                              // For doubles, allow payout regardless of hasEntry
                              if (sideEvent.applies_to === 'doubles' || result.hasEntry) {
                                const data = {
                                  bowlerId:
                                    sideEvent.applies_to === 'team'
                                      ? result.team_id
                                      : sideEvent.applies_to === 'doubles'
                                        ? result.bowler_ids
                                        : result.bowler_id,
                                  bowlerName:
                                    sideEvent.applies_to === 'team'
                                      ? result.team_name
                                      : sideEvent.applies_to === 'doubles'
                                        ? (result.bowler_names ? result.bowler_names.join(' & ') : result.bowler_ids?.join(' & '))
                                        : result.bowler_name,
                                  sideEventId: sideEvent.id,
                                  appliesTo: sideEvent.applies_to,
                                  sideEventType: 'side_event',
                                  amount: 0,
                                  placement: 1,
                                  leagueId: leagueId,
                                };
                                setSelectedMysterySeriesResult(data);
                                setShowMysterySeriesPayoutModal(true);
                              }
                            }}
                            disabled={sideEvent.applies_to !== 'doubles' && !result.hasEntry}
                            className={`px-3 py-1 rounded text-sm transition duration-200 ${sideEvent.applies_to === 'doubles' || result.hasEntry
                              ? 'bg-purple-600 hover:bg-purple-700 text-white'
                              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                              }`}
                          >
                            {sideEvent.applies_to === 'doubles' || result.hasEntry ? t('manageSideEvent.payoutButton') : t('manageSideEvent.noEntryButton')}
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : recalculatedMysterySeriesResults.mysterySeries ? (
                    <div className="text-center py-4">
                      <div className="text-red-400 font-bold text-lg flex items-center justify-center">
                        ❌ {t('manageSideEvent.mysterySeriesNotBowled', { series: recalculatedMysterySeriesResults.mysterySeries })}
                      </div>
                      <div className="text-red-300 text-sm mt-2">
                        {t('manageSideEvent.tryDifferentSeries')}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Clean Game Management Section */}
        {sideEvent.type === 'clean_game' && cleanGameStats && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">{t('manageSideEvent.cleanGameManagement')}</h2>
              <div className="text-right">
                <div className="text-green-300 text-sm">{t('manageSideEvent.totalPot')}</div>
                <div className="text-white text-2xl font-bold">${Number(cleanGameStats.totalPot || 0).toFixed(2)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Total Entries */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-green-300 text-sm mb-2">{t('manageSideEvent.totalEntries')}</div>
                <div className="text-white text-3xl font-bold">{cleanGameStats.totalEntries}</div>
              </div>

              {/* Side Event Details */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-green-300 text-sm mb-2">{t('manageSideEvent.gameNumbers')}</div>
                <div className="text-white text-lg font-bold">
                  {cleanGameStats.sideEvent.applies_to_game_numbers || t('manageSideEvent.allGames')}
                </div>
                <div className="text-green-300 text-sm mt-2">
                  {t('manageSideEvent.cleanGame')}
                </div>
              </div>

              {/* Clean Game Description */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-green-300 text-sm mb-2">{t('manageSideEvent.requirements')}</div>
                <div className="text-white text-sm">
                  {t('manageSideEvent.noOpenFrames')}
                </div>
                <div className="text-green-300 text-xs mt-2">
                  {t('manageSideEvent.adminVerifies')}
                </div>
              </div>
            </div>

            {/* Winner Selection Section */}
            <div className="bg-white/10 border border-white/20 rounded-lg p-4 mb-6">
              <h3 className="text-xl font-bold text-white mb-4">{t('manageSideEvent.selectCleanGameWinner')}</h3>

              {/* Search Input */}
              <div className="flex space-x-4 mb-4">
                <div className="flex-1">
                  <input
                    type="text"
                    value={cleanGameQuery}
                    onChange={(e) => setCleanGameQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchCleanGameBowlers()}
                    placeholder={t('manageSideEvent.bowlerNameOrId')}
                    className="w-full bg-gray-800 text-white border border-gray-600 rounded px-4 py-2"
                  />
                </div>
                <button
                  onClick={searchCleanGameBowlers}
                  disabled={!cleanGameQuery.trim() || cleanGameQuery.trim().length < 2}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg transition duration-200"
                >
                  {t('manageSideEvent.search')}
                </button>
              </div>

              {/* Search Results */}
              {cleanGameSearchResults && (
                <div className="mt-4">
                  {cleanGameSearchResults.results.length > 0 ? (
                    <div className="space-y-2">
                      <div className="text-green-300 text-sm mb-3">
                        Found {cleanGameSearchResults.results.length} registered bowler(s):
                      </div>
                      {cleanGameSearchResults.results.map((bowler: any) => (
                        <div key={bowler.id} className="flex items-center justify-between bg-green-500/10 border border-green-400/30 rounded-lg p-4">
                          <div className="flex-1">
                            <div className="text-white font-bold text-lg">{bowler.name}</div>
                            <div className="text-green-300 text-sm">
                              ID: {bowler.id}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setSelectedCleanGameWinner(bowler)}
                              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition duration-200"
                            >
                              {t('manageSideEvent.selectWinner')}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="text-red-400 text-lg">No registered bowlers found</div>
                      <div className="text-red-300 text-sm mt-2">
                        Only bowlers registered for this side event can win
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Selected Winner */}
              {selectedCleanGameWinner && (
                <div className="mt-6 p-4 bg-green-500/20 border border-green-400 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-green-300 text-sm">{t('manageSideEvent.selectedCleanGameWinner')}:</div>
                      <div className="text-white font-bold text-xl">{selectedCleanGameWinner.name}</div>
                      <div className="text-green-300 text-sm">
                        ID: {selectedCleanGameWinner.id}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedCleanGameWinner(null)}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition duration-200"
                      >
                        {t('manageSideEvent.clear')}
                      </button>
                      <button
                        onClick={() => {

                          const data = {
                            bowlerId: selectedCleanGameWinner.id,
                            bowlerName: selectedCleanGameWinner.name,
                            appliesTo: sideEvent.applies_to,
                            sideEventId: sideEvent.id,
                            sideEventType: 'side_event',
                            amount: Number(cleanGameStats.totalPot || 0).toFixed(2),
                            placement: '1st',
                            leagueId: leagueId
                          };

                          setSelectedCleanGameResult(data);
                          setShowCleanGamePayoutModal(true);
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition duration-200"
                      >
                        {t('manageSideEvent.processPayout')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Survivor Management Section */}
        {sideEvent.type === 'survivor' && survivorStats && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">{t('manageSideEvent.survivorManagement')}</h2>
              <div className="text-right">
                <div className="text-red-300 text-sm">{t('manageSideEvent.totalPot')}</div>
                <div className="text-white text-2xl font-bold">${Number(survivorStats.totalPot || 0).toFixed(2)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Total Entries */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-red-300 text-sm mb-2">{t('manageSideEvent.totalEntries')}</div>
                <div className="text-white text-3xl font-bold">{survivorStats.totalEntries}</div>
              </div>

              {/* Final Survivors */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-red-300 text-sm mb-2">{t('manageSideEvent.finalSurvivors')}</div>
                <div className="text-white text-3xl font-bold">
                  {recalculatedSurvivorRounds.length > 0
                    ? recalculatedSurvivorRounds[recalculatedSurvivorRounds.length - 1]?.survivorsCount || 0
                    : survivorStats.totalEntries}
                </div>
              </div>

              {/* Elimination Rate */}
              <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                <div className="text-red-300 text-sm mb-2">{t('manageSideEvent.eliminationRate')}</div>
                <div className="text-white text-lg font-bold">
                  {survivorStats.totalEntries > 0
                    ? (((survivorStats.totalEntries - (recalculatedSurvivorRounds.length > 0
                      ? recalculatedSurvivorRounds[recalculatedSurvivorRounds.length - 1]?.survivorsCount || 0
                      : survivorStats.totalEntries)) / survivorStats.totalEntries) * 100).toFixed(1)
                    : 0}%
                </div>
              </div>
            </div>

            {/* Survivor Rounds */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-white mb-4">{t('manageSideEvent.survivorRounds')}</h3>

              {survivorStats.survivorConfig.map((config: any, index: number) => {
                // Use recalculated round data instead of original
                const roundData = recalculatedSurvivorRounds.find(r => r.gameNumber === config.game_number);
                const isEditing = editingSurvivorScore?.gameNumber === config.game_number;

                return (
                  <div key={config.game_number} className="bg-white/10 border border-white/20 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <h4 className="text-lg font-bold text-white">{t('manageSideEvent.game')} {config.game_number}</h4>

                        {/* Editable Survivor Score */}
                        <div className="flex items-center space-x-2">
                          <span className="text-red-300 text-sm">{t('manageSideEvent.survivorScore')}:</span>
                          {isEditing ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                min="0"
                                max="1200"
                                value={editingSurvivorScore?.score || ''}
                                onChange={(e) => setEditingSurvivorScore(prev => prev ? {
                                  ...prev,
                                  score: e.target.value
                                } : null)}
                                className="w-20 bg-gray-800 text-white border border-gray-600 rounded px-2 py-1 text-sm"
                              />
                              <button
                                onClick={handleSurvivorScoreSave}
                                className="bg-green-600 hover:bg-green-700 text-white p-1 rounded transition duration-200"
                              >
                                ✓
                              </button>
                              <button
                                onClick={handleSurvivorScoreCancel}
                                className="bg-red-600 hover:bg-red-700 text-white p-1 rounded transition duration-200"
                              >
                                ✗
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span className="text-white font-bold text-lg">{config.survivor_score}</span>
                              <button
                                onClick={() => handleSurvivorScoreEdit(config.game_number, config.survivor_score)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs transition duration-200"
                              >
                                Edit
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Round Statistics */}
                      {roundData && (
                        <div className="text-right">
                          <div className="text-red-300 text-sm">
                            {t('manageSideEvent.started')}: {roundData.participantsStart} → {t('manageSideEvent.survivors')}: {roundData.survivorsCount}
                          </div>
                          <div className="text-red-400 text-sm">
                            {t('manageSideEvent.eliminated')}: {roundData.eliminated}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Survivors List */}
                    {roundData && roundData.survivors.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-red-300 text-sm mb-2">
                          {t('manageSideEvent.survivors')} ({roundData.survivorsCount}):
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {roundData.survivors.map((survivor: any, survivorIndex: number) => (
                            <div key={survivorIndex} className="bg-green-500/10 border border-green-400/30 rounded-lg p-3">
                              <div className="text-white font-bold">
                                {sideEvent.applies_to === 'team'
                                  ? survivor.team_name
                                  : sideEvent.applies_to === 'doubles'
                                    ? (survivor.bowler_names
                                      ? survivor.bowler_names.join(' & ')
                                      : Array.isArray(survivor.bowler_names)
                                        ? survivor.bowler_names.join(' & ')
                                        : t('manageSideEvent.doublesPair'))
                                    : survivor.bowler_name}
                              </div>
                              <div className="text-green-300 text-sm">
                                Score: {isHandicap ? survivor.handicapScore || survivor.actualScore : survivor.scratchScore || survivor.actualScore} ({t('manageSideEvent.needed')}: {roundData.survivorScore}) {isHandicap ? t('manageSideEvent.withHandicap') : t('manageSideEvent.scratchScore')}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Round Payout Button */}
                        {roundData.survivorsCount > 0 && (
                          <div className="mt-4">
                            <button
                              onClick={() => {
                                setShowSurvivorPayoutModal(true);
                                setSelectedSurvivorResult({
                                  survivors: roundData.survivors,
                                  roundPayout: config.round_payout,
                                  gameNumber: config.game_number,
                                  appliesTo: sideEvent.applies_to,
                                  sideEventId: sideEvent.id,
                                  leagueId,
                                });
                              }}
                              className="bg-green-500/50 hover:bg-green-600/50 text-white px-4 py-2 rounded-lg transition duration-200 text-sm"
                            >
                              {t('manageSideEvent.processRoundPayout', { round: config.game_number })} ({config.round_payout ? `$${config.round_payout}` : '$0'})
                            </button>
                            <div className="text-xs text-green-300 mt-1">
                              {t('manageSideEvent.payoutAmountCanBeChanged')}
                            </div>
                          </div>
                        )}

                        {/* Final Payout Button (only show on last round) */}
                        {index === survivorStats.survivorConfig.length - 1 && roundData.survivorsCount > 0 && (
                          <div className="mt-4 text-center">
                            <button
                              onClick={() => {
                                setShowSurvivorPayoutModal(true);
                                setSelectedSurvivorResult({
                                  survivors: roundData.survivors,
                                  roundPayout: config.round_payout,
                                  gameNumber: config.game_number,
                                  appliesTo: sideEvent.applies_to,
                                  sideEventId: sideEvent.id,
                                  leagueId,
                                });
                              }}
                              className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-2 rounded-lg transition duration-200"
                            >
                              {t('manageSideEvent.processAllRoundsSurvivorPayouts', { amount: config.final_payout ? `$${config.final_payout}` : '$0' })}
                            </button>
                            <div className="text-xs text-yellow-300 mt-1">
                              * {t('manageSideEvent.payoutAmountCanBeChangedOnPayoutScreen')}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* No Survivors Message */}
                    {roundData && roundData.survivors.length === 0 && (
                      <div className="text-center py-4">
                        <div className="text-red-400 text-lg">No Survivors</div>
                        <div className="text-red-300 text-sm">
                          All participants eliminated in Game {config.game_number}
                        </div>
                      </div>
                    )}

                    {/* No Scores Yet */}
                    {!roundData && (
                      <div className="text-center py-4">
                        <div className="text-gray-400 text-lg">Game {config.game_number} Not Played Yet</div>
                        <div className="text-gray-500 text-sm">
                          Scores will appear here once the game is completed
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Eliminator Management Section */}
        {sideEvent.type === 'eliminator' && eliminatorStats && (
          <div className="bg-orange-900/20 backdrop-blur-lg rounded-xl p-8 border border-orange-500/30">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">🔥</div>
              <h2 className="text-3xl font-bold text-orange-300 mb-2">{t('manageSideEvent.eliminatorManagement')}</h2>
              <p className="text-orange-200">
                {eliminatorStats.totalEntries} {t('manageSideEvent.participants').toLowerCase()} • ${eliminatorStats.totalPot} {t('manageSideEvent.totalPot').toLowerCase()}
              </p>
            </div>

            <div className="space-y-6">
              {eliminatorStats.eliminatorConfig.map((config: any, index: number) => {
                // Use recalculated round data instead of original
                const roundData = recalculatedEliminatorRounds.find((r: any) => r.gameNumber === config.game_number);

                return (
                  <div key={config.id} className="bg-orange-800/30 rounded-lg p-6 border border-orange-600/40">
                    {/* Game Header */}
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-orange-300">{t('manageSideEvent.game')} {config.game_number}</h3>
                        <div className="text-orange-400 text-sm">
                          {t('manageSideEvent.advancementRate', { percentage: config.advancement_percentage })}
                        </div>
                      </div>

                      {/* Editable Advancement Percentage */}
                      <div className="text-right">
                        <div className="text-orange-300 text-sm mb-1">{t('manageSideEvent.advancementPercentage')}</div>
                        <div className="flex items-center space-x-2">
                          {editingEliminatorPercentage?.gameNumber === config.game_number ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                min="25"
                                max="75"
                                value={editingEliminatorPercentage?.percentage || ''}
                                onChange={(e) => setEditingEliminatorPercentage(prev =>
                                  prev ? { ...prev, percentage: e.target.value } : null
                                )}
                                className="w-20 px-2 py-1 rounded bg-orange-800/50 border border-orange-600 text-white text-center"
                              />
                              <button
                                onClick={handleEliminatorPercentageSave}
                                className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs transition duration-200"
                              >
                                ✓
                              </button>
                              <button
                                onClick={handleEliminatorPercentageCancel}
                                className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs transition duration-200"
                              >
                                ✗
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span className="text-white font-bold text-lg">{config.advancement_percentage}%</span>
                              <button
                                onClick={() => handleEliminatorPercentageEdit(config.game_number, config.advancement_percentage)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs transition duration-200"
                              >
                                Edit
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Round Statistics */}
                      {roundData && (
                        <div className="text-right">
                          <div className="text-orange-300 text-sm">
                            {t('manageSideEvent.started')}: {roundData.participantsStart} → {t('manageSideEvent.advanced')}: {roundData.advancersCount}
                          </div>
                          <div className="text-orange-400 text-sm">
                            {t('manageSideEvent.eliminated')}: {roundData.eliminated} • {t('manageSideEvent.cutoff')}: {roundData.cutoffScore}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Advancers List */}
                    {roundData && roundData.advancers.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-orange-300 text-sm mb-2">
                          {t('manageSideEvent.advancers')} ({roundData.advancersCount}):
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {roundData.advancers.map((advancer: any, advancerIndex: number) => (
                            <div key={advancerIndex} className="bg-green-500/10 border border-green-400/30 rounded-lg p-3">
                              <div className="text-white font-bold">
                                {sideEvent.applies_to === 'team'
                                  ? advancer.team_name
                                  : sideEvent.applies_to === 'doubles'
                                    ? (advancer.bowler_names
                                      ? advancer.bowler_names.join(' & ')
                                      : Array.isArray(advancer.bowler_names)
                                        ? advancer.bowler_names.join(' & ')
                                        : t('manageSideEvent.doublesPair'))
                                    : advancer.bowler_name}
                              </div>
                              <div className="text-green-300 text-sm">
                                Score: {isHandicap ? advancer.handicapScore || advancer.actualScore : advancer.scratchScore || advancer.actualScore} ({t('manageSideEvent.cutoff')}: {roundData.cutoffScore}) {isHandicap ? t('manageSideEvent.withHandicap') : t('manageSideEvent.scratchScore')}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Round Payout Button */}
                        {roundData.advancersCount > 0 && (
                          <div className="mt-4">
                            <button
                              onClick={() => {
                                // Navigate to payout component
                                setShowEliminatorPayoutModal(true);
                                setSelectedEliminatorResult({
                                  advancers: roundData.advancers,
                                  roundPayout: config.round_payout,
                                  gameNumber: config.game_number,
                                  appliesTo: sideEvent.applies_to,
                                  sideEventId: sideEvent.id,
                                  leagueId,
                                });
                              }}
                              className="bg-green-500/50 hover:bg-green-600/50 text-white px-4 py-2 rounded-lg transition duration-200 text-sm"
                            >
                              {t('manageSideEvent.processRoundPayout', { round: config.game_number })} ({config.round_payout ? `$${config.round_payout}` : '$0'})
                            </button>
                            <div className="text-xs text-green-300 mt-1">
                              {t('manageSideEvent.payoutAmountCanBeChanged')}
                            </div>
                          </div>
                        )}

                        {/* Final Payout Button (only show on last round) */}
                        {index === eliminatorStats.eliminatorConfig.length - 1 && roundData.advancersCount > 0 && (
                          <div className="mt-4 text-center">
                            <button
                              onClick={() => {
                                // Navigate to payout component 
                                setShowEliminatorPayoutModal(true);
                                setSelectedEliminatorResult({
                                  advancers: roundData.advancers,
                                  roundPayout: config.round_payout,
                                  gameNumber: config.game_number,
                                  appliesTo: sideEvent.applies_to,
                                  sideEventId: sideEvent.id,
                                  leagueId,
                                });
                              }}
                              className="bg-orange-600/70 hover:bg-orange-700/70 text-white px-6 py-2 rounded-lg transition duration-200"
                            >
                              {t('manageSideEvent.processFinalPayouts')} ({config.final_payout ? `$${config.final_payout}` : '$0'})
                            </button>
                            <div className="text-xs text-orange-300 mt-1">
                              * {t('manageSideEvent.payoutAmountCanBeChanged')}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* No Advancers Message */}
                    {roundData && roundData.advancers.length === 0 && (
                      <div className="text-center py-4">
                        <div className="text-orange-400 text-lg">No Advancers</div>
                        <div className="text-orange-300 text-sm">
                          All participants eliminated in Game {config.game_number}
                        </div>
                      </div>
                    )}

                    {/* No Scores Yet */}
                    {!roundData && (
                      <div className="text-center py-4">
                        <div className="text-gray-400 text-lg">Game {config.game_number} Not Played Yet</div>
                        <div className="text-gray-500 text-sm">
                          Scores will appear here once the game is completed
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Side Pot Management Section */}
        {sideEvent.type === 'side_pots' && sidePotStats && (
          <div className="bg-purple-900/20 backdrop-blur-lg rounded-xl p-8 border border-purple-500/30">
            <div className=" mb-8">
              <h2 className="text-3xl font-bold text-purple-300 mb-2">{t('manageSideEvent.sidePotManagement')}</h2>
              <p className="text-purple-200">
                {sidePotStats.totalEntries} {t('manageSideEvent.participants').toLowerCase()} • ${sidePotStats.totalPot} {t('manageSideEvent.totalPot').toLowerCase()}
              </p>
            </div>

            <div className="space-y-6">
              {/* Use recalculated side pot results instead of original */}
              {recalculatedSidePotResults.map((result: any, index: number) => (
                <div key={result.configId} className="bg-purple-800/30 rounded-lg p-6 border border-purple-600/40">
                  {/* Side Pot Header */}
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-purple-300">
                        {result.configType === 'individual_game'
                          ? t('manageSideEvent.sidePotGame', { game: result.gameIdentifier })
                          : t('manageSideEvent.sidePotGames', { games: result.gameIdentifier })
                        }
                      </h3>
                      <div className="text-purple-400 text-sm">
                        {result.configType === 'individual_game'
                          ? t('manageSideEvent.highestScoreInGame', { game: result.gameIdentifier })
                          : t('manageSideEvent.highestTotalAcrossGames', { games: result.gameIdentifier })
                        }
                      </div>
                    </div>

                    {/* Payout Info */}
                    <div className="text-right">
                      <div className="text-purple-300 text-sm mb-1">{t('manageSideEvent.totalSuggestedPayout')}</div>
                      <div className="text-white font-bold text-lg">${result.payoutAmount}</div>

                    </div>
                  </div>

                  {/* Winners List */}
                  {result.winners.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-purple-300 text-sm mb-2">
                        {t('manageSideEvent.winners')} ({result.winners.length}):
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {result.winners.map((winner: any, winnerIndex: number) => (
                          <div key={winnerIndex} className="bg-yellow-500/10 border border-yellow-400/30 rounded-lg p-3">
                            <div className="text-white font-bold">
                              {sideEvent.applies_to === 'team'
                                ? winner.team_name
                                : sideEvent.applies_to === 'doubles'
                                  ? (winner.bowler_names
                                    ? winner.bowler_names.join(' & ')
                                    : Array.isArray(winner.bowler_names)
                                      ? winner.bowler_names.join(' & ')
                                      : t('manageSideEvent.doublesPair'))
                                  : winner.bowler_name}
                            </div>
                            <div className="text-yellow-300 text-sm">
                              {result.configType === 'individual_game'
                                ? `Score: ${isHandicap ? winner.handicapScore || winner.totalScore : winner.scratchScore || winner.totalScore} ${isHandicap ? t('manageSideEvent.withHandicap') : t('manageSideEvent.scratchScore')}`
                                : `Total: ${isHandicap ? winner.handicapScore || winner.totalScore : winner.scratchScore || winner.totalScore} ${isHandicap ? t('manageSideEvent.withHandicap') : t('manageSideEvent.scratchScore')}`
                              }
                            </div>
                            <div className="text-yellow-400 text-xs">
                              {t('manageSideEvent.rank')}: #{winner.rank}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Payout Button */}
                      <div className="mt-4 text-center">
                        <button
                          onClick={() => {
                            setSelectedSidePotResult(result);
                            setShowSidePotPayoutModal(true);
                          }}
                          className="bg-purple-500/60 hover:bg-purple-600/60 text-white px-4 py-2 rounded-lg transition duration-200 text-sm"
                        >
                          {t('manageSideEvent.processPayout')} (${result.payoutAmount})
                        </button>
                        <div className="text-xs text-purple-300 mt-1">
                          {t('manageSideEvent.payoutAmountCanBeChanged')}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* No Winners Message */}
                  {result.winners.length === 0 && (
                    <div className="text-center py-4">
                      <div className="text-purple-400 text-lg">No Scores Available</div>
                      <div className="text-purple-300 text-sm">
                        {result.configType === 'individual_game'
                          ? `Game ${result.gameIdentifier} has not been played yet`
                          : `Games ${result.gameIdentifier} have not been completed yet`
                        }
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* No Side Pot Configs */}
              {recalculatedSidePotResults.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-purple-400 text-lg">No Side Pot Configurations</div>
                  <div className="text-purple-300 text-sm">
                    Side pot configurations will appear here once they are set up
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* BRACKET MANAGEMENT SECTION */}
        {sideEvent.type === 'bracket' && (
          <div className="bg-gradient-to-br from-purple-800/50 via-purple-700/40 to-indigo-800/50 border border-purple-400/30 rounded-xl p-4">
            <div className="flex items-center mb-3">
              <svg className="w-5 h-5 text-purple-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-bold text-white">{t('manageSideEvent.bracketManagement')}</h3>
            </div>

            {selectedSquadId ? (
              <div>
                {bracketStatus && bracketStatus.hasStartedBowling ? (
                  <div className="bg-red-500/20 border border-red-400 rounded-lg p-3">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 18.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span className="text-red-300 font-medium text-sm">{t('manageSideEvent.errors.bowlingHasStarted')}</span>
                    </div>
                  </div>
                ) : bracketStatus && bracketStatus.canCreateBrackets ? (
                  <div className="bg-green-500/20 border border-green-400 rounded-lg p-3">
                    <div className="flex items-center mb-2">
                      <svg className="w-4 h-4 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-green-300 font-medium text-sm">Ready for Bracket Entries</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="bg-green-900/30 rounded p-2">
                        <p className="text-green-300">Bracket Size</p>
                        <p className="text-white font-bold">{bracketStatus.bracketSize}</p>
                      </div>
                      <div className="bg-green-900/30 rounded p-2">
                        <p className="text-green-300">Max Brackets</p>
                        <p className="text-white font-bold">{bracketStatus.maxPossibleBrackets}</p>
                      </div>
                    </div>
                  </div>
                ) : bracketStatus ? (
                  <div className="bg-orange-500/20 border border-orange-400 rounded-lg p-3">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-orange-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 18.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span className="text-orange-300 font-medium text-sm">Cannot create brackets - {bracketStatus.message}</span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-blue-500/20 border border-blue-400 rounded-lg p-3">
                    <p className="text-blue-200 text-sm">Loading bracket status...</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-blue-500/20 border border-blue-400 rounded-lg p-3">
                <p className="text-blue-200 text-sm">Select a squad to check bracket status</p>
              </div>
            )}
          </div>
        )}

        {/* Bracket Entries Top Bar */}
        {sideEvent.type === 'bracket' && bracketStatus && bracketStatus.canCreateBrackets && (
          <div className="bg-gradient-to-r from-purple-600/20 via-blue-600/20 to-indigo-600/20 border border-purple-300/30 rounded-lg p-3 mt-3">
            <div className="flex items-center justify-between">
              {/* Left side - Entry counts */}
              <div className="flex items-center space-x-4 text-xs">
                <div className="bg-purple-900/30 rounded px-3 py-1">
                  <span className="text-purple-300">{t('manageSideEvent.bracketEntries')}:</span>{' '}
                  <span className="text-white font-bold">{bracketEntries.totalEntries}</span>
                </div>
                <div className="bg-blue-900/30 rounded px-3 py-1">
                  <span className="text-blue-300">{t('manageSideEvent.currentBracketCount')}:</span>{' '}
                  <span className="text-white font-bold">{bracketEntries.currentBracketCount}</span>
                </div>
              </div>

              {/* Right side - Horizontally scrollable bowler list */}
              <div className="flex-1 ml-4">
                {bracketEntries.bowlerEntries.length > 0 ? (
                  <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-purple-500 scrollbar-track-purple-900/20">
                    <div className="flex space-x-2 pb-2">
                      {bracketEntries.bowlerEntries.map((bowler, index) => (
                        <div
                          key={index}
                          className="bg-gray-800/50 rounded px-2 py-1 whitespace-nowrap text-xs flex-shrink-0 flex items-center gap-2"
                        >
                          <div>
                            <span className="text-gray-300">{bowler.name}:</span>{' '}
                            <span className="text-white font-medium">{bowler.entryCount}</span>
                            {(bowler as any).refundAmount > 0 && (
                              <span className="text-red-400 ml-1">(must refund {(bowler as any).refundAmount})</span>
                            )}
                          </div>
                          {/* Delete button - only show when brackets NOT generated */}
                          {!bracketsGenerated && (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Remove one entry for ${bowler.name}?`)) return;
                                try {
                                  const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
                                  const response = await fetch(`${apiUrl}/admin/manage-side-events/side-events/${actualSideEventId}/bracket-entry`, {
                                    method: 'DELETE',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                                    },
                                    body: JSON.stringify({
                                      bowlerId: bowler.bowler_id,
                                      teamId: bowler.team_id
                                    })
                                  });

                                  if (response.ok) {
                                    const result = await response.json();
                                    window.alert(`Entry refunded: $${result.refundAmount}`);
                                    fetchBracketEntries(); // Refresh the list
                                  } else {
                                    const error = await response.json();
                                    window.alert(error.error || 'Failed to refund entry');
                                  }
                                } catch (err) {
                                  console.error('Error refunding entry:', err);
                                  window.alert('Failed to refund entry');
                                }
                              }}
                              className="text-red-400 hover:text-red-300 transition-colors"
                              title="Remove one entry"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-400 text-xs">No entries yet</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Generate/Delete/Lock Brackets Buttons */}
        {sideEvent.type === 'bracket' && bracketStatus && bracketEntries.currentBracketCount > 0 && (
          <div className="flex gap-3 mt-3">
            {!bracketsGenerated ? (
              /* Only show generate button if bowling hasn't started */
              bracketStatus.canCreateBrackets && (
                <button
                  onClick={generateBrackets}
                  disabled={loading}
                  className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-600 disabled:to-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  {loading ? t('manageSideEvent.processing') : t('manageSideEvent.generateBrackets')}
                </button>
              )
            ) : (
              <>
                {/* Show delete button only if brackets are NOT locked AND bowling hasn't started */}
                {!bracketsLocked && bracketStatus.canCreateBrackets && (
                  <button
                    onClick={deleteBrackets}
                    disabled={loading}
                    className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:from-gray-600 disabled:to-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {loading ? 'Deleting...' : 'Delete Unlocked Brackets'}
                  </button>
                )}

                {/* Lock/Unlock buttons */}
                {!bracketsLocked ? (
                  <button
                    onClick={handleLockBrackets}
                    disabled={loading}
                    className="flex items-center gap-2 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 disabled:from-gray-600 disabled:to-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    {loading ? 'Locking...' : 'Lock Brackets'}
                  </button>
                ) : (
                  <>
                    {/* Unlock button - hide after scores processed */}
                    {!bracketScoresProcessed && (
                      <button
                        onClick={handleUnlockBrackets}
                        disabled={loading}
                        className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                        </svg>
                        {loading ? 'Unlocking...' : 'Unlock Brackets'}
                      </button>
                    )}

                    {/* Pull Scores button - only when locked and scores not processed */}
                    {!bracketScoresProcessed && (
                      <button
                        onClick={handlePullScores}
                        disabled={loading}
                        className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        {loading ? 'Processing...' : 'Pull Scores'}
                      </button>
                    )}

                    {/* Payout and Export buttons - only when scores are processed */}
                    {bracketScoresProcessed && (
                      <>
                        <button
                          onClick={() => setShowPayoutModal(true)}
                          disabled={loading}
                          className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-600 disabled:to-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                          {t('manageSideEvent.handlePayouts')}
                        </button>
                        {bracketResults && (
                          <BracketPrintView
                            bracketResults={bracketResults}
                            totalBracketPot={totalBracketPot}
                            sideEventName={sideEvent.name}
                            leagueName={sideEvent.league_name}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Generated Brackets Display */}
        {sideEvent.type === 'bracket' && bracketsGenerated && generatedBrackets.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-bold text-white">
                {t('manageSideEvent.generatedBrackets')} {bracketsLocked && <span className="text-yellow-400 text-sm ml-2">(🔒 {t('manageSideEvent.locked')})</span>}
              </h3>
              {/* Only show reshuffle button if brackets are not locked */}
              {!bracketsLocked && (
                <button
                  onClick={reshuffleBrackets}
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reshuffle
                </button>
              )}
            </div>

            {/* Refund Information and Bracket Pot */}
            <div className="mb-4 space-y-2">
              {refundedEntries.length > 0 && (
                <div className="bg-yellow-500/20 border border-yellow-400 rounded-lg p-3">
                  <div className="text-yellow-300 text-sm font-medium mb-2">💰 Refunded Entries:</div>
                  {refundedEntries.map((refund, index) => (
                    <div key={index} className="text-yellow-200 text-sm">
                      {refund.playerName} was refunded {refund.entriesRefunded} entries
                    </div>
                  ))}
                </div>
              )}
              {totalBracketPot > 0 && (
                <div className="bg-green-500/20 border border-green-400 rounded-lg p-3">
                  <div className="text-green-300 text-sm font-medium">
                    💰 {t('manageSideEvent.totalBracketsPot')}: ${Number(totalBracketPot || 0).toFixed(2)}
                  </div>
                </div>
              )}
            </div>

            {/* Compact Bracket Results Summary - show when scores are processed */}
            {bracketScoresProcessed && bracketResults && (
              <div className="mt-4 space-y-4">
                {/* Overall Results Header */}
                <div className="bg-gradient-to-r from-purple-800/40 to-indigo-800/40 backdrop-blur-lg rounded-lg p-4 border border-purple-400/30">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      🏆 {t('manageSideEvent.tournamentResults')}
                    </h3>
                    <div className="text-purple-300 text-sm font-semibold">
                      {t('manageSideEvent.pot')}: ${Number(totalBracketPot || 0).toFixed(2)}
                    </div>
                  </div>

                  {/* Winners Row */}
                  <div className="flex flex-wrap gap-2">
                    {bracketResults.winners.map((winner, index) => (
                      <div key={index} className="bg-gradient-to-r from-green-600 to-emerald-600 border border-green-400 rounded-md px-3 py-2 flex items-center gap-2 shadow-lg">
                        <span className="text-lg">🥇</span>
                        <div>
                          <div className="text-white font-bold text-sm">{winner.playerName}</div>
                          <div className="text-green-100 text-xs">${winner.payout.toFixed(2)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tournament Bracket Results - Clean Card Layout */}
                {(() => {
                  // Group rounds by bracket
                  const bracketGroups = new Map();

                  bracketResults.rounds.forEach((round, index) => {
                    const bracketNumber = Math.floor(index / (bracketResults.rounds.filter(r => r.roundNumber === 1).length > 1
                      ? bracketResults.rounds.length / bracketResults.rounds.filter(r => r.roundNumber === 1).length
                      : bracketResults.rounds.length)) + 1;

                    if (!bracketGroups.has(bracketNumber)) {
                      bracketGroups.set(bracketNumber, []);
                    }
                    bracketGroups.get(bracketNumber).push(round);
                  });

                  return Array.from(bracketGroups.entries()).map(([bracketNumber, rounds]) => {
                    const bracketSize = bracketStatus?.bracketSize || 8;
                    const totalRounds = Math.log2(bracketSize);

                    // Organize matchups by round
                    const roundData = Array.from({ length: totalRounds }, () => []);

                    rounds.forEach((round: any) => {
                      const roundIndex = round.roundNumber - 1;
                      if (roundData[roundIndex]) {
                        roundData[roundIndex] = round.matchups;
                      }
                    });

                    return (
                      <div key={bracketNumber} className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-lg rounded-lg border border-slate-400/30 overflow-hidden">
                        {/* Bracket Header */}
                        <div className="bg-gradient-to-r from-indigo-600/40 to-blue-600/40 px-4 py-2 border-b border-slate-400/20">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-bold text-white flex items-center gap-2">
                              <div className="w-6 h-6 bg-indigo-500 rounded-md flex items-center justify-center text-white font-bold text-xs">
                                {bracketNumber}
                              </div>
                              Bracket #{bracketNumber} - Results
                            </h4>
                            <div className="text-indigo-200 text-xs">
                              {rounds.length} Round{rounds.length !== 1 ? 's' : ''}
                            </div>
                          </div>
                        </div>

                        {/* Clean Round-by-Round Results */}
                        <div className="p-4 space-y-4">
                          {roundData.map((roundMatchups, roundIndex) => {
                            const roundName = roundIndex === totalRounds - 1 ? '🏆 Final' :
                              roundIndex === totalRounds - 2 ? 'Semifinal' :
                                `Round ${roundIndex + 1}`;

                            return (
                              <div key={roundIndex} className="bg-slate-800/40 rounded-lg p-4">
                                <div className="text-center text-white font-bold text-sm mb-4 border-b border-purple-400/30 pb-2">
                                  {roundName}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {roundMatchups.map((matchup: any, matchIndex: number) => {
                                    const isFinal = roundIndex === totalRounds - 1;
                                    
                                    return (
                                      <div key={matchIndex} className="bg-slate-700/50 rounded-lg overflow-hidden border border-slate-600">
                                        <div className="bg-slate-600/50 px-3 py-1 text-xs text-slate-300 font-semibold flex items-center justify-between">
                                          <span>Match {matchIndex + 1}</span>
                                          {isFinal && <span className="text-yellow-300">🏆 Championship</span>}
                                        </div>
                                        <div className="p-3 space-y-2">
                                          {/* Participant 1 with score */}
                                          <div className={`flex items-center justify-between rounded px-3 py-2 ${
                                            matchup.winnerId === matchup.entry1.id
                                              ? isFinal
                                                ? 'bg-yellow-600/30 border border-yellow-400'
                                                : 'bg-green-600/30 border border-green-400'
                                              : 'bg-slate-600/30'
                                          }`}>
                                            <span className="text-white font-medium text-sm">{matchup.entry1.name}</span>
                                            <div className="flex items-center gap-2">
                                              <span className="text-white font-bold">{matchup.entry1.score}</span>
                                              {matchup.winnerId === matchup.entry1.id && (
                                                <span className={isFinal ? 'text-yellow-300' : 'text-green-300'}>✓</span>
                                              )}
                                            </div>
                                          </div>
                                          
                                          <div className="text-center text-xs text-slate-400 font-semibold">VS</div>
                                          
                                          {/* Participant 2 with score or BYE */}
                                          {matchup.entry2 ? (
                                            <div className={`flex items-center justify-between rounded px-3 py-2 ${
                                              matchup.winnerId === matchup.entry2.id
                                                ? isFinal
                                                  ? 'bg-yellow-600/30 border border-yellow-400'
                                                  : 'bg-green-600/30 border border-green-400'
                                                : 'bg-slate-600/30'
                                            }`}>
                                              <span className="text-white font-medium text-sm">{matchup.entry2.name}</span>
                                              <div className="flex items-center gap-2">
                                                <span className="text-white font-bold">{matchup.entry2.score}</span>
                                                {matchup.winnerId === matchup.entry2.id && (
                                                  <span className={isFinal ? 'text-yellow-300' : 'text-green-300'}>✓</span>
                                                )}
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="flex items-center justify-center bg-amber-900/20 rounded px-3 py-2 border border-amber-600/30">
                                              <span className="text-amber-300 font-medium text-sm">BYE</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}

            <div className="grid gap-6">
              {/* Only show brackets if scores haven't been processed */}
              {!bracketScoresProcessed && generatedBrackets.map((bracket) => {
                const bracketSize = bracketStatus?.bracketSize || 8;
                const totalRounds = Math.log2(bracketSize);
                
                return (
                  <div key={bracket.id} className="bg-gradient-to-r from-purple-600/20 via-blue-600/20 to-indigo-600/20 border border-purple-300/30 rounded-lg p-6">
                    {/* Bracket Header */}
                    <div className="flex items-center justify-between mb-6">
                      <h4 className="text-xl font-bold text-white flex items-center gap-2">
                        <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                          {bracket.bracket_number}
                        </div>
                        Bracket #{bracket.bracket_number}
                      </h4>
                      <div className="text-sm text-purple-300">
                        {bracket.participants.filter(p => p.name !== 'BYE').length} participants
                      </div>
                    </div>

                    {/* Simplified List View - Much Cleaner */}
                    <div className="space-y-6">
                      {/* Round 1 Matchups */}
                      <div className="bg-slate-800/40 rounded-lg p-4">
                        <div className="text-center text-white font-bold text-sm mb-4 border-b border-purple-400/30 pb-2">
                          Round 1 Matchups
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Array.from({ length: Math.ceil(bracket.participants.length / 2) }, (_, matchIndex) => {
                            const participant1 = bracket.participants[matchIndex * 2];
                            const participant2 = bracket.participants[matchIndex * 2 + 1];
                            
                            if (!participant1) return null;
                            
                            return (
                              <div key={matchIndex} className="bg-slate-700/50 rounded-lg overflow-hidden border border-slate-600">
                                <div className="bg-slate-600/50 px-3 py-1 text-xs text-slate-300 font-semibold">
                                  Match {matchIndex + 1}
                                </div>
                                <div className="p-3 space-y-2">
                                  {/* Participant 1 */}
                                  <div className="flex items-center justify-between bg-slate-600/30 rounded px-3 py-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-bold text-purple-400">#{participant1.seed}</span>
                                      <span className="text-white font-medium text-sm">{participant1.name}</span>
                                    </div>
                                  </div>
                                  
                                  {/* VS Divider */}
                                  <div className="text-center text-xs text-slate-400 font-semibold">VS</div>
                                  
                                  {/* Participant 2 */}
                                  {participant2 ? (
                                    <div className="flex items-center justify-between bg-slate-600/30 rounded px-3 py-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-bold text-purple-400">#{participant2.seed}</span>
                                        <span className="text-white font-medium text-sm">{participant2.name}</span>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-center bg-amber-900/20 rounded px-3 py-2 border border-amber-600/30">
                                      <span className="text-amber-300 font-medium text-sm">BYE</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Future Rounds Placeholder */}
                      {totalRounds > 1 && (
                        <div className="bg-slate-800/40 rounded-lg p-4">
                          <div className="text-center">
                            <div className="text-purple-300 font-semibold mb-2">📋 Bracket Progress</div>
                            <div className="flex items-center justify-center gap-4 text-sm">
                              {Array.from({ length: totalRounds }, (_, i) => {
                                const roundName = i === totalRounds - 1 ? '🏆 Final' :
                                  i === totalRounds - 2 ? 'Semifinal' :
                                  `Round ${i + 1}`;
                                
                                return (
                                  <div key={i} className="flex items-center gap-2">
                                    <div className={`px-3 py-1 rounded ${
                                      i === 0 
                                        ? 'bg-green-600/30 text-green-300 border border-green-500/50' 
                                        : 'bg-slate-700/50 text-slate-400 border border-slate-600'
                                    }`}>
                                      {roundName}
                                    </div>
                                    {i < totalRounds - 1 && (
                                      <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                      </svg>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <div className="text-xs text-slate-400 mt-3">
                              Winners will advance after bowling and locking brackets
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Bracket Summary */}
                    <div className="mt-6 pt-4 border-t border-slate-600/50">
                      <div className="flex justify-center items-center gap-6 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-purple-400">👥</span>
                          <span className="text-slate-400">Participants:</span>
                          <span className="text-white font-bold">{bracket.participants.filter(p => p.name !== 'BYE').length}</span>
                        </div>
                        <div className="w-px h-4 bg-slate-600"></div>
                        <div className="flex items-center gap-2">
                          <span className="text-purple-400">🎯</span>
                          <span className="text-slate-400">Rounds:</span>
                          <span className="text-white font-bold">{totalRounds}</span>
                        </div>
                        <div className="w-px h-4 bg-slate-600"></div>
                        <div className="flex items-center gap-2">
                          <span className="text-purple-400">📊</span>
                          <span className="text-slate-400">Status:</span>
                          <span className="text-green-400 font-semibold">Ready to Lock</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Placeholder for other event types */}
        {!['high_game', 'high_series', 'mystery_score', 'mystery_series', 'clean_game', 'survivor', 'eliminator', 'side_pots', 'bracket'].includes(sideEvent.type) && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 text-center">
            <div className="text-6xl mb-4">🏆</div>
            <h2 className="text-2xl font-bold text-white mb-4">Event-Specific Management</h2>
            <p className="text-blue-200 mb-6">
              Additional functionality based on side event type will be displayed here.
            </p>
            <div className="bg-blue-500/20 border border-blue-400 rounded-lg p-4">
              <p className="text-blue-200 font-semibold">Coming Soon!</p>
              <p className="text-blue-300 text-sm">
                {sideEvent.type === 'bracket' && 'Bracket management, seeding, and matchup controls'}
                {sideEvent.type === 'side_pots' && 'Side pot configuration and payout tracking'}
                {sideEvent.type === 'custom' && 'Custom event management features'}
                {!['bracket', 'high_game', 'high_series', 'mystery_score', 'mystery_series', 'clean_game', 'survivor', 'side_pots', 'eliminator', 'custom'].includes(sideEvent.type) && 'Event-specific management features'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Remove Side Event Modal */}
      {showRemoveModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-red-900 via-red-800 to-red-700 rounded-xl p-6 border border-white/20 max-w-lg w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">🗑️ Remove Side Event</h3>
              <button
                onClick={() => setShowRemoveModal(false)}
                className="text-white/70 hover:text-white transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="bg-white/10 rounded-lg p-6 text-center mb-6">
              <div className="text-6xl mb-4">⚠️</div>
              <h4 className="text-xl font-bold text-white mb-4">Are you sure?</h4>
              <p className="text-red-200 mb-4">
                This action will permanently remove the side event and cannot be undone.
              </p>

              {/* Side Event Details */}
              <div className="bg-red-800/30 rounded-lg p-4 text-left border border-red-600/40">
                <h5 className="text-red-300 font-semibold mb-3">Side Event Details:</h5>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-red-300">Name:</span>
                    <span className="text-white font-medium">{sideEvent.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-red-300">Type:</span>
                    <span className="text-white">{sideEvent.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-red-300">Cost:</span>
                    <span className="text-white">${sideEvent.cost}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-red-300">Applies to:</span>
                    <span className="text-white">{sideEvent.applies_to}</span>
                  </div>
                  {sideEvent.squad_name && (
                    <div className="flex justify-between">
                      <span className="text-red-300">Squad:</span>
                      <span className="text-white">{sideEvent.squad_name}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-red-300">Current Entries:</span>
                    <span className="text-white font-bold">{currentEntriesCount}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setShowRemoveModal(false)}
                disabled={removingEvent}
                className="flex-1 bg-gray-600 hover:bg-gray-700 disabled:opacity-50 text-white font-bold py-3 px-4 rounded-lg transition duration-200"
              >
                Cancel
              </button>
              <button
                onClick={removeSideEvent}
                disabled={removingEvent}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
              >
                {removingEvent ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Removing...
                  </>
                ) : (
                  'Remove Side Event'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lock Brackets Modal */}
      {showLockModal && lockModalType === 'lock' && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-yellow-900 via-yellow-800 to-orange-800 rounded-xl p-6 border border-white/20 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">🔒 Lock Brackets</h3>
              <button
                onClick={() => {
                  setShowLockModal(false);
                  setLockModalType(null);
                }}
                className="text-white/70 hover:text-white transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="bg-white/10 rounded-lg p-6 text-center">
              <div className="text-yellow-400 text-6xl mb-4">⚠️</div>
              <h4 className="text-xl font-bold text-white mb-4">Are you sure you want to lock the brackets?</h4>

              <div className="space-y-3 text-left max-w-md mx-auto mb-6">
                <p className="text-white/80 text-sm">
                  This will save the current matchups in the database.
                </p>
                <p className="text-yellow-300 text-sm font-medium">
                  ⚠️ Keep in mind brackets can't be unlocked once bowling has started.
                </p>
              </div>

              <div className="flex space-x-3 justify-center">
                <button
                  onClick={() => {
                    setShowLockModal(false);
                    setLockModalType(null);
                  }}
                  className="bg-gray-600/50 hover:bg-gray-600/70 text-white px-6 py-2 rounded-lg transition duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={lockBrackets}
                  disabled={loading}
                  className="bg-yellow-600/80 hover:bg-yellow-600 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg transition duration-200 font-bold"
                >
                  {loading ? 'Locking...' : 'Lock Brackets'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unlock Brackets Modal */}
      {showUnlockModal && lockModalType === 'unlock' && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 rounded-xl p-6 border border-white/20 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">🔓 Unlock Brackets</h3>
              <button
                onClick={() => {
                  setShowUnlockModal(false);
                  setLockModalType(null);
                }}
                className="text-white/70 hover:text-white transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="bg-white/10 rounded-lg p-6 text-center">
              <div className="text-blue-400 text-6xl mb-4">❓</div>
              <h4 className="text-xl font-bold text-white mb-4">Are you sure you want to unlock the brackets?</h4>

              <div className="space-y-3 text-left max-w-md mx-auto mb-6">
                <p className="text-white/80 text-sm">
                  This will remove the currently saved matchups from the database.
                </p>
                <p className="text-blue-300 text-sm">
                  You'll be able to reshuffle and make changes again.
                </p>
              </div>

              <div className="flex space-x-3 justify-center">
                <button
                  onClick={() => {
                    setShowUnlockModal(false);
                    setLockModalType(null);
                  }}
                  className="bg-gray-600/50 hover:bg-gray-600/70 text-white px-6 py-2 rounded-lg transition duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={unlockBrackets}
                  disabled={loading}
                  className="bg-blue-600/80 hover:bg-blue-600 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg transition duration-200 font-bold"
                >
                  {loading ? 'Unlocking...' : 'Unlock Brackets'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cannot Unlock Modal */}
      {showUnlockModal && lockModalType === 'cannotUnlock' && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-red-900 via-red-800 to-red-700 rounded-xl p-6 border border-white/20 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">🚫 Cannot Unlock</h3>
              <button
                onClick={() => {
                  setShowUnlockModal(false);
                  setLockModalType(null);
                }}
                className="text-white/70 hover:text-white transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="bg-white/10 rounded-lg p-6 text-center">
              <div className="text-red-400 text-6xl mb-4">🛑</div>
              <h4 className="text-xl font-bold text-white mb-4">Brackets can't be unlocked after bowling has started</h4>

              <div className="space-y-3 text-left max-w-md mx-auto mb-6">
                <p className="text-white/80 text-sm">
                  Games have already been played for this squad, so the brackets are now permanently locked.
                </p>
                <p className="text-red-300 text-sm">
                  Unlocking is only allowed when no scores have been saved.
                </p>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={() => {
                    setShowUnlockModal(false);
                    setLockModalType(null);
                  }}
                  className="bg-red-600/80 hover:bg-red-600 text-white px-6 py-2 rounded-lg transition duration-200"
                >
                  Understood
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pull Scores Confirmation Modal */}
      {showPullScoresConfirmModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-800 rounded-xl p-6 border border-white/20 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">🎯 Confirm Score Type</h3>
              <button
                onClick={() => setShowPullScoresConfirmModal(false)}
                className="text-white/70 hover:text-white transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="bg-white/10 rounded-lg p-6 text-center">
              <div className="text-yellow-400 text-6xl mb-4">⚠️</div>
              <h4 className="text-xl font-bold text-white mb-4">Pull Bracket Scores</h4>

              <div className="space-y-3 text-left max-w-md mx-auto mb-6">
                <p className="text-white/80 text-sm">
                  You are about to pull scores using <strong className={isHandicap ? 'text-blue-400' : 'text-green-400'}>
                    {isHandicap ? 'HANDICAP' : 'SCRATCH'}</strong> scoring.
                </p>
                <p className="text-yellow-300 text-sm font-medium">
                  ⚠️ This action cannot be undone. The bracket will be permanently scored with {isHandicap ? 'handicap' : 'scratch'} values.
                </p>
                <p className="text-white/70 text-xs">
                  The toggle will be locked after pulling scores.
                </p>
              </div>

              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setShowPullScoresConfirmModal(false)}
                  className="bg-gray-600/80 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmPullScores}
                  disabled={loading}
                  className={`px-6 py-2 rounded-lg font-medium transition duration-200 ${isHandicap
                    ? 'bg-blue-600/80 hover:bg-blue-600 text-white'
                    : 'bg-green-600/80 hover:bg-green-600 text-white'
                    }`}
                >
                  {loading ? 'Processing...' : `Pull ${isHandicap ? 'Handicap' : 'Scratch'} Scores`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Missing Scores Modal */}
      {showScoresModal && scoresModalType === 'missing' && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-orange-900 via-orange-800 to-red-800 rounded-xl p-6 border border-white/20 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">📊 Missing Scores</h3>
              <button
                onClick={() => {
                  setShowScoresModal(false);
                  setScoresModalType(null);
                }}
                className="text-white/70 hover:text-white transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="bg-white/10 rounded-lg p-6 text-center">
              <div className="text-orange-400 text-6xl mb-4">⚠️</div>
              <h4 className="text-xl font-bold text-white mb-4">Scores haven't been captured yet</h4>

              <div className="space-y-3 text-left max-w-md mx-auto mb-6">
                <p className="text-white/80 text-sm">
                  Not all games in this squad have scores recorded for all bowlers.
                </p>
                <p className="text-orange-300 text-sm font-medium">
                  📝 Please capture scores on Schedule → Matches first.
                </p>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={() => {
                    setShowScoresModal(false);
                    setScoresModalType(null);
                  }}
                  className="bg-orange-600/80 hover:bg-orange-600 text-white px-6 py-2 rounded-lg transition duration-200"
                >
                  Understood
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Entry Receipt Modal */}
      {entryReceipt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 rounded-xl p-6 border border-white/20 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">🧾 Entry Receipt</h3>
              <button
                onClick={() => setEntryReceipt(null)}
                className="text-white/70 hover:text-white transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="bg-white/10 rounded-lg p-6 text-center">
              <div className="text-green-400 text-6xl mb-4">✅</div>
              <h4 className="text-2xl font-bold text-white mb-4">Entry Processed Successfully!</h4>

              <div className="space-y-3 text-left max-w-md mx-auto">
                <div className="flex justify-between">
                  <span className="text-white/80">Side Event:</span>
                  <span className="text-white font-medium">{entryReceipt.side_event_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/80">Bowler:</span>
                  <span className="text-white font-medium">{entryReceipt.bowler_name}</span>
                </div>
                {entryReceipt.team_name && (
                  <div className="flex justify-between">
                    <span className="text-white/80">Team:</span>
                    <span className="text-white font-medium">{entryReceipt.team_name}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-white/80">Squad:</span>
                  <span className="text-white font-medium">{entryReceipt.squad_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/80">Date:</span>
                  <span className="text-white font-medium">
                    {new Date(entryReceipt.squad_date).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/80">Applies To:</span>
                  <span className="text-white font-medium capitalize">{entryReceipt.applies_to}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/80">Entries:</span>
                  <span className="text-white font-medium">{entryReceipt.entry_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/80">Cost per Entry:</span>
                  <span className="text-white font-medium">${Number(entryReceipt.cost_per_entry).toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-t border-white/20 pt-3">
                  <span className="text-white/80 font-bold">Total Paid:</span>
                  <span className="text-green-300 font-bold">${Number(entryReceipt.total_cost).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/80">Payment Date:</span>
                  <span className="text-white font-medium">
                    {new Date(entryReceipt.payment_date).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="mt-6 space-x-3">
                <button
                  onClick={() => window.print()}
                  className="bg-blue-600/50 hover:bg-blue-600/70 text-white px-4 py-2 rounded-lg transition duration-200"
                >
                  🖨️ Print Receipt
                </button>
                <button
                  onClick={() => setEntryReceipt(null)}
                  className="bg-green-600/50 hover:bg-green-600/70 text-white px-4 py-2 rounded-lg transition duration-200"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bracket Payout Modal */}
      {bracketResults && (
        <BracketPayoutModal
          isOpen={showPayoutModal}
          onClose={() => setShowPayoutModal(false)}
          winners={bracketResults.winners}
          sideEventId={actualSideEventId!}
          leagueId={leagueId}
          onPayoutComplete={(entryId, amount) => {
            // Optionally refresh bracket results or update UI
            console.log(`Payout completed`);
          }}
        />
      )}

      {/* Side Pot Payout Modal */}
      {selectedSidePotResult && (
        <SidePotPayoutModal
          isOpen={showSidePotPayoutModal}
          onClose={() => {
            setShowSidePotPayoutModal(false);
            setSelectedSidePotResult(null);
          }}
          winners={selectedSidePotResult.winners}
          sideEventId={actualSideEventId!}
          leagueId={leagueId}
          defaultAmount={selectedSidePotResult.payoutPerWinner}
          appliesTo={sideEvent.applies_to as 'individual' | 'team' | 'doubles'}
          onPayoutComplete={(winnerId, amount) => {
            // Optionally refresh side pot results or update UI
            console.log(`Side pot payout completed`);
          }}
        />
      )}


      {/* clean game payout modal */}
      {selectedCleanGameResult && (
        <CleanGamePayoutModal
          isOpen={showCleanGamePayoutModal}
          onClose={() => {
            setShowCleanGamePayoutModal(false);
            setSelectedCleanGameResult(null);
          }}
          bowlerId={selectedCleanGameResult.bowlerId}
          bowlerName={selectedCleanGameResult.bowlerName}
          sideEventId={selectedCleanGameResult.sideEventId}
          leagueId={selectedCleanGameResult.leagueId}
          defaultAmount={selectedCleanGameResult.amount}
          appliesTo={selectedCleanGameResult.appliesTo}
          onPayoutComplete={(winnerId, amount) => {
            // Optionally refresh clean game results or update UI
            console.log(`Clean game payout completed`);
          }}
        />
      )}


      {/* High game payout modal */}
      {selectedHighGameResult && (
        <HighGamePayoutModal
          isOpen={showHighGamePayoutModal}
          onClose={() => {
            setShowHighGamePayoutModal(false);
            setSelectedHighGameResult(null);
          }}
          bowlerId={selectedHighGameResult.bowlerId}
          bowlerName={selectedHighGameResult.bowlerName}
          sideEventId={selectedHighGameResult.sideEventId}
          leagueId={selectedHighGameResult.leagueId}
          defaultAmount={selectedHighGameResult.amount}
          appliesTo={selectedHighGameResult.appliesTo}
          onPayoutComplete={(winnerId, amount) => {
            // Optionally refresh high game results or update UI
            console.log(`High game payout completed`);
          }}
        />
      )}

      {/* High series payout modal */}
      {selectedHighSeriesResult && (
        <HighSeriesPayoutModal
          isOpen={showHighSeriesPayoutModal}
          onClose={() => {
            setShowHighSeriesPayoutModal(false);
            setSelectedHighSeriesResult(null);
          }}
          bowlerId={selectedHighSeriesResult.bowlerId}
          bowlerName={selectedHighSeriesResult.bowlerName}
          sideEventId={selectedHighSeriesResult.sideEventId}
          leagueId={selectedHighSeriesResult.leagueId}
          defaultAmount={selectedHighSeriesResult.amount}
          appliesTo={selectedHighSeriesResult.appliesTo}
          onPayoutComplete={(winnerId, amount) => {
            // Optionally refresh high series results or update UI
            console.log(`High series payout completed`);
          }}
        />
      )}

      {/* Mystery score payout modal */}
      {selectedMysteryScoreResult && (
        <MysteryScorePayoutModal
          isOpen={showMysteryScorePayoutModal}
          onClose={() => {
            setShowMysteryScorePayoutModal(false);
            setSelectedMysteryScoreResult(null);
          }}
          bowlerId={selectedMysteryScoreResult.bowlerId}
          bowlerName={selectedMysteryScoreResult.bowlerName}
          sideEventId={selectedMysteryScoreResult.sideEventId}
          leagueId={selectedMysteryScoreResult.leagueId}
          defaultAmount={selectedMysteryScoreResult.amount}
          appliesTo={selectedMysteryScoreResult.appliesTo}
          onPayoutComplete={(winnerId, amount) => {
            // Optionally refresh mystery score results or update UI
            console.log(`Mystery score payout completed`);
          }}
        />
      )}

      {/* Mystery series payout modal */}
      {selectedMysterySeriesResult && (
        <MysterySeriesPayoutModal
          isOpen={showMysterySeriesPayoutModal}
          onClose={() => {
            setShowMysterySeriesPayoutModal(false);
            setSelectedMysterySeriesResult(null);
          }}
          bowlerId={selectedMysterySeriesResult.bowlerId}
          bowlerName={selectedMysterySeriesResult.bowlerName}
          sideEventId={selectedMysterySeriesResult.sideEventId}
          leagueId={selectedMysterySeriesResult.leagueId}
          defaultAmount={selectedMysterySeriesResult.amount}
          appliesTo={selectedMysterySeriesResult.appliesTo}
          onPayoutComplete={(winnerId, amount) => {
            // Optionally refresh mystery series results or update UI
            console.log(`Mystery series payout completed`);
          }}
        />
      )}

      {/* Survivor payout modal */}
      {selectedSurvivorResult && (
        <SurvivorPayoutModal
          isOpen={showSurvivorPayoutModal}
          onClose={() => {
            setShowSurvivorPayoutModal(false);
            setSelectedSurvivorResult(null);
          }}
          winners={selectedSurvivorResult.survivors}
          sideEventId={actualSideEventId!}
          leagueId={leagueId}
          defaultAmount={0}
          appliesTo={sideEvent.applies_to as 'individual' | 'team' | 'doubles'}
          onPayoutComplete={(winnerId, amount) => {
            // Optionally refresh survivor results or update UI
            console.log(`Survivor payout completed`);
          }}
        />
      )}

      {/* Eliminator payout modal */}
      {selectedEliminatorResult && (
        <EliminatorPayoutModal
          isOpen={showEliminatorPayoutModal}
          onClose={() => {
            setShowEliminatorPayoutModal(false);
            setSelectedEliminatorResult(null);
          }}
          winners={selectedEliminatorResult.advancers}
          sideEventId={actualSideEventId!}
          leagueId={leagueId}
          defaultAmount={0}
          appliesTo={sideEvent.applies_to as 'individual' | 'team' | 'doubles'}
          onPayoutComplete={(winnerId, amount) => {
            // Optionally refresh eliminator results or update UI
            console.log(`Eliminator payout completed`);
          }}
        />
      )}

    </div>
  );
};

export default ManageSideEvent;